/**
 * Las pantallas de la estación envasado, del boceto `../Perejil/public/poc/embolsado.html`
 * (iteración 1, 14/9) y del contrato de `docs/esquema.md` §5.7. Primera tanda —el camino
 * normal—: el inicio con los pallets en armado, embolsar un precinto (con «pallet nuevo»
 * adentro), abrir un pallet y cerrarlo con el rótulo a la vista. La tarima de espera y «se tira
 * un pallet» llegaron con la devolución de la planta. Desde el 27/10/2026 (§12.21) «una tarima es un
 * precinto»: con la letra, el precinto vuelve a la lista con sus bolsas en espera y embolsarlo las
 * reparte (`en_tarima`); el flujo aparte «pasar a pallet» se fue.
 *
 * Misma armazón que producción: un paso por pantalla, borrador persistido en cada cambio,
 * revisión final tocable y confirmación manteniendo apretado. Lo que se ve es la PROYECCIÓN
 * (§5.7 r. 21): el catálogo de pallets abiertos con lo que este teléfono hizo encima. El número
 * del pallet es el del rótulo emitido que se le pone al abrir (r. 22, tercera versión del 14/9):
 * «pallet nuevo» pregunta qué rótulo, y nada espera un número. La hora se propone al nacer el
 * borrador y se revisa en la última pantalla, como el descarte del silo.
 */

import { BorradorYaConfirmado, encolar, IncidenteResuelto, incidenteConDescendientes, mutacionesDeAvisos, mutacionesDeCalidad, mutacionesDeEmbolsado, pendientesDe, RotuloEnCola, type MutacionAEncolar } from '../datos/cola';
import { textoValido } from '../nucleo/produccion';
import { NOMBRE_LETRA } from '../nucleo/muestra';
import { cajaDeTexto, cuantasNotas, listaDeNotas, notaParaLaCola } from './notas';
import { ENTIDADES_EMBOLSADO, type Entidad, type FilaOutbox, type Resumen } from '../datos/bd';
import { generarUlid } from '../../src/ulid';
import {
	admiteRemanente,
	armarPayloadCargaAnular,
	armarPayloadCargaCorregir,
	armarPayloadEnvasadoCrear,
	armarPayloadPalletAbrir,
	armarPayloadPalletAnular,
	armarPayloadPalletCerrar,
	armarPayloadPalletUnir,
	BOLSAS_DE_TOLERANCIA,
	BOLSAS_ESTANDAR,
	bolsasNuevas,
	bolsasValidas,
	cargaDentroDelPallet,
	cierreDePalletValido,
	DIAS_DE_ALERTA,
	diasDesde,
	esLaUnicaDeUnCerrado,
	fechaCorta,
	FORMATO_PREMARCADO,
	FORMATOS_BOLSA,
	formatoTexto,
	gramosDeCarga,
	kgConDecimales,
	campaniaDeApertura,
	cargasPorPrecinto,
	destinosDelPrecinto,
	LETRAS_MUESTRA,
	letraDestinoDe,
	masNuevaDe,
	masUnAnio,
	MAX_PERSONAS_PALLET,
	MOTIVOS_ANULACION_CARGA,
	MOTIVOS_ANULACION_PALLET,
	motivoDeAnularCarga,
	mutacionesAlRehacer,
	NOMBRE_LETRA_PALLET,
	numeroDeApertura,
	numerosDeAperturas,
	padreDeApertura,
	padreDeCarga,
	padreDeTarima,
	padreDeTransicion,
	partida,
	porQueNoPasaAPallet,
	porQueNoSeCorrige,
	precintoEntero,
	precintosDe,
	repartoDeBolsas,
	rotulosPara,
	rotuloTomado,
	saldoG,
	sobrepasaElSilo,
	superaLaTolerancia,
	superaLaToleranciaAlCorregir,
	TOLERANCIA_SOBRE_NETO,
	venceEn,
	type BorradorAnularCarga,
	type BorradorAnularPallet,
	type BorradorCambiarLetra,
	type BorradorCerrarPallet,
	type BorradorCorregirCarga,
	type BorradorEmbolsado,
	type BorradorEmbolsar,
	type BorradorNotaPrecinto,
	type BorradorRehacerPallet,
	type CargaVista,
	type EnvasadoServidor,
	type FilaPendiente,
	type LetraPallet,
	type MutacionCaida,
	type PalletCerradoProyectado,
	type PalletProyectado,
	type PalletVisto,
	type PrecintoVisto,
	type RotuloDelCatalogo,
	type RotuloElegido,
	type TarimaDelPrecinto,
	type TarimaUnidaServidor,
} from '../nucleo/embolsado';
import { cuandoCorto, fechaDeProduccion, HORA_MS, HORAS_PARA_CORREGIR, instanteLocal, msDeInstanteLocal, NOMBRE_TURNO, turnoDe } from '../nucleo/tiempo';
import { botonMantener, el, flag, icono, kg, padAnclado, PALETA, teclado, tile } from './componentes';
import {
	acciones,
	ahoraMs,
	app,
	campaniaAbiertaId,
	conPersonaHabitual,
	descartarBorrador,
	guardarBorrador,
	irA,
	nuevoBorradorRehacerPallet,
	numeroDeSilo,
	palletsAbiertos,
	personaPremarcada,
	recalcularAvisos,
	recalcularPallets,
	recalcularParaMuestra,
	recalcularRotulosEnBandeja,
} from './estado';
import { grillaPersonas, nombreDe, pregunta, reloj } from './pantallas-captura';

/** Los pasos de cada flujo, en orden. */
const PASOS = {
	// El pallet nace con su primera carga: «pallet nuevo» vive adentro de embolsar (y de pasar una tarima) y
	// no es un flujo aparte (Nicolás, 2/10: abría pallets vacíos con «cerrar ›» que no se podían cerrar).
	embolsar: ['quien', 'precinto', 'bolsas', 'pallet', 'rotulo', 'personas', 'hora', 'revisar'],
	cerrar_pallet: ['quien', 'cual', 'hora', 'revisar'],
	rehacer_pallet: ['rotulo', 'revisar'],
	// Segunda tanda (boceto «Se tira un pallet»): la tarima de espera va por `embolsar` con `tarima`, y desde el 27/10 (§12.21)
	// pasarla a un pallet también (`en_tarima`): el flujo aparte `unir_tarima` se retiró (`TIPOS_RETIRADOS`).
	anular_pallet: ['quien', 'cual', 'motivo', 'hora', 'revisar'],
	// v6 (§12.5, §12.4): desde el precinto elegido.
	cambiar_letra: ['quien', 'nueva_letra', 'motivo_letra', 'revisar'],
	nota_precinto: ['quien', 'texto_nota', 'revisar'],
	// §12.18: desde la carga tocada, en el pallet en armado o entre los cerrados de 48 horas.
	corregir_carga: ['quien', 'bolsas_carga', 'revisar'],
	anular_carga: ['quien', 'motivo_carga', 'revisar'],
} as const satisfies Record<BorradorEmbolsado['tipo'], readonly string[]>;

/** Los motivos con que el servidor rechaza una apertura por su rótulo (§5.7 r. 22), dichos para la planta: se rehacen desde la bandeja con otro. */
export const QUE_PASO_ROTULO: Record<string, string> = {
	rotulo_no_emitido: 'ese número de rótulo no figura emitido en esta campaña: la oficina no lo imprimió',
	rotulo_anulado: 'la oficina anuló ese rótulo; si es el papel que tenés, que lo reactive',
	rotulo_usado: 'ese rótulo ya está en otro pallet',
	rotulo_de_otra_letra: 'ese rótulo es de otra letra: no va en este pallet',
	rotulo_de_otro_formato: 'ese rótulo es de otro formato de bolsa: no va en este pallet',
};
export const MOTIVOS_DE_ROTULO = new Set(Object.keys(QUE_PASO_ROTULO));

export function pasosDe(b: BorradorEmbolsado): string[] {
	return [...PASOS[b.tipo]];
}

/** El color de cada letra, el del boceto: Q verde, P ámbar, L azul, D rojizo; la tarima, gris. */
const COLOR_LETRA: Record<string, string> = { Q: '#1B7A3C', P: '#B8760A', L: '#1E5FA8', D: '#8C2E10' };
const comprometiendo = new Set<string>();
/**
 * Por qué se volvió a un paso desde la confirmación: se muestra mientras el borrador siga en ESE paso.
 * Antes se borraba en el primer pintado, y el repintado que hace el sync al terminar una pasada (también
 * una fallida) se lo llevaba antes de que la embolsadora lo leyera (visto en el pane, 2/10).
 */
let avisoDeRevision: { capture_id: string; paso: number; texto: string } | null = null;
/**
 * «¿Qué precinto?» muestra los embolsados enteros solo si se pide: desaparecen, pero un remanente se puede
 * anotar. UNA sola vista: `plegados`, `ultimos` (los `ENTEROS_A_LA_VISTA` últimos cerrados) o `todos`
 * («Ver los N más viejos»). Y es DEL BORRADOR, como `avisoDeRevision`: un borrador nuevo arranca plegado
 * por cualquier entrada —el inicio, «Otro precinto» de la pantalla de listo, «Embolsar este precinto»
 * tras un desvío— y el mismo borrador la conserva entre repintados. Las dos rondas de Codex del 19/10:
 * con dos banderas, volver al inicio plegaba una sola; con un reset solo en el inicio, «Otro precinto»
 * (que no pasa por el inicio) traía los 693 al borrador siguiente.
 */
export type VistaDeEnteros = 'plegados' | 'ultimos' | 'todos';
let vistaDeEnteros: { capture_id: string; vista: VistaDeEnteros } | null = null;

/** La vista de los enteros para este borrador: plegados, salvo que sea el borrador que la fijó. */
export function vistaDeEnterosDe(b: Pick<BorradorEmbolsar, 'capture_id'>): VistaDeEnteros {
	return vistaDeEnteros?.capture_id === b.capture_id ? vistaDeEnteros.vista : 'plegados';
}

export function fijarVistaDeEnteros(b: Pick<BorradorEmbolsar, 'capture_id'>, vista: VistaDeEnteros): void {
	vistaDeEnteros = { capture_id: b.capture_id, vista };
}

/**
 * Los pasos por los que se navega hacia adelante. Los salteados siguen siendo alcanzables
 * tocando su fila en la revisión: saltear no es esconder.
 */
export function indicesDe(b: BorradorEmbolsado): number[] {
	const pasos = pasosDe(b);
	const salteados = new Set<string>();
	// Teléfono personal: el «¿quién?» ya está contestado (§5.4).
	if (b.personas.length > 0) salteados.add('quien');
	// La hora se propone «ahora» al nacer y se ve en la revisión, como en el descarte del silo.
	salteados.add('hora');
	// Se entró tocando la tarjeta del precinto en el inicio: ya está elegido.
	if (b.tipo === 'embolsar' && b.desde_inicio && b.precinto !== null) salteados.add('precinto');
	// Quiénes embolsan y qué rótulo se preguntan al abrir el pallet: en uno abierto ya están.
	if (b.tipo === 'embolsar' && b.nuevo === null) {
		salteados.add('personas');
		salteados.add('rotulo');
	}
	// La tarima de espera no tiene pallet, rótulo ni quiénes: la arma quien carga (boceto «Tarima de espera»).
	if (b.tipo === 'embolsar' && b.tarima) {
		salteados.add('pallet');
		salteados.add('rotulo');
		salteados.add('personas');
	}
	// Se entró tocando la tarjeta del pallet (o de la tarima) en el inicio: ya está elegido.
	if ((b.tipo === 'cerrar_pallet' || b.tipo === 'anular_pallet') && b.desde_inicio && b.pallet !== null) salteados.add('cual');
	return pasos.map((_, i) => i).filter((i) => !salteados.has(pasos[i]));
}

/** Deja el borrador parado en su primer paso visible: lo que main usa al arrancar un flujo. */
export function arrancarEn(b: BorradorEmbolsado): void {
	b.paso = indicesDe(b)[0] ?? 0;
}

function nombrePaso(b: BorradorEmbolsado): string {
	return pasosDe(b)[b.paso] ?? 'revisar';
}

/** Con `aviso`, es un rebote desde la confirmación: el paso dice por qué se volvió hasta que se lo deja. Sin él, cualquier aviso anterior se va. */
async function irAlPaso(b: BorradorEmbolsado, paso: number, aviso?: string): Promise<void> {
	avisoDeRevision = aviso === undefined ? null : { capture_id: b.capture_id, paso, texto: aviso };
	b.paso = paso;
	await guardarBorrador(b).catch((error) => console.error('no se pudo guardar el borrador', error));
	app.render();
}

/** Por POSICIÓN, como en producción: desde un paso salteado, «Siguiente» sigue hacia adelante. */
async function avanzar(b: BorradorEmbolsado): Promise<void> {
	const siguiente = indicesDe(b).find((i) => i > b.paso);
	if (siguiente !== undefined) await irAlPaso(b, siguiente);
}

async function retroceder(b: BorradorEmbolsado): Promise<void> {
	const anterior = [...indicesDe(b)].reverse().find((i) => i < b.paso);
	if (anterior === undefined) {
		// Desde la primera pantalla, volver es volver al inicio: el borrador recién nace.
		await descartarBorrador(b).catch(() => null);
		acciones.inicioEnvasado();
		return;
	}
	await irAlPaso(b, anterior);
}

function pie(pieNodo: HTMLElement, b: BorradorEmbolsado, derecho?: { texto: string; deshabilitado?: boolean; alTocar: () => void }): void {
	const volver = el('button', { class: 'btn ghost', 'aria-label': 'Volver', type: 'button' });
	volver.appendChild(icono('volver', 24, 2.6));
	volver.onclick = () => void retroceder(b);
	pieNodo.appendChild(volver);
	if (derecho) {
		const boton = el('button', { class: 'btn go', type: 'button' }, derecho.texto);
		if (derecho.deshabilitado) boton.setAttribute('disabled', '');
		boton.onclick = derecho.alTocar;
		pieNodo.appendChild(boton);
	}
}

function guardarYPintar(b: BorradorEmbolsado): void {
	void guardarBorrador(b);
	app.render();
}

// ── Lo que se ve: precintos con letra y pallets en armado ─────────────────

/**
 * El número es el del rótulo que se le puso al abrir (r. 22). Se resuelve por identidad contra
 * lo que este teléfono ya sabe (`palletsConocidos`) y, si no, por lo que trae el propio objeto:
 * así un pallet que gestión renumeró se ve con el número nuevo apenas baja el catálogo.
 */
export function numeroDePallet(p: { pallet_id?: string; numero: number | null }): number | null {
	return (p.pallet_id === undefined ? undefined : app.palletsConocidos.get(p.pallet_id)?.numero) ?? p.numero;
}

export function nombreDePallet(p: { pallet_id?: string; numero: number | null }): string {
	const numero = numeroDePallet(p);
	return numero === null ? 'sin número' : `Nº ${numero}`;
}

/** La tarima no lleva número: se nombra por su hora y quiénes la armaron («tarima de las 14:20 · Yani»). */
export function nombreDeTarima(t: { abierto_en: string; personas: string[] }): string {
	const hora = t.abierto_en.slice(11, 16);
	return `de las ${hora || '—'}${t.personas.length > 0 ? ` · ${personasTexto(t.personas)}` : ''}`;
}

function personasTexto(ids: string[]): string {
	const nombres = ids.map(nombreDe);
	if (nombres.length <= 1) return nombres[0] ?? '—';
	return `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1]}`;
}

function nombreDeLote(loteId: string): string {
	return (app.catalogos?.lotes_campo ?? []).find((l) => l.id === loteId)?.nombre ?? '—';
}

/**
 * Los precintos con letra que esperan embolsarse (§5.5 `llenados_para_envasar`), el más viejo
 * primero. Lo que el catálogo no refleja —cargas pendientes o con acuse más nuevo que el
 * snapshot, aunque su pallet ya se haya cerrado acá— se descuenta del saldo y fija el formato:
 * un precinto embolsado con 7,25 sigue siendo de 7,25 aunque el catálogo todavía no lo diga
 * (Codex, ronda del diff, 14/9).
 */
export function precintosParaEmbolsar(): Array<PrecintoVisto & { cerrado_en: string }> {
	// §12.21: la tarima en espera del precinto, si la letra ya llegó y el silo sigue cerrado (si no, la tarjeta de la
	// tarima sigue en el inicio con su traba y el precinto no está en esta lista).
	const tarimasListas = palletsAbiertos().filter((t) => t.es_tarima === 1 && t.bolsas > 0 && trabaDeLaTarima(t) === null);
	return (app.catalogos?.llenados_para_envasar ?? [])
		.map((l) => {
			const local = app.saldosDeEmbolsado.get(l.llenado_id);
			const tarima = tarimasListas.find((t) => t.envasados[0]?.llenado_id === l.llenado_id);
			return {
				en_tarima: tarima === undefined ? null : tarimaDelPrecinto(tarima),
				llenado_id: l.llenado_id,
				precinto: l.precinto,
				silo_numero: numeroDeSilo({ silo_fisico_id: l.silo_fisico_id }),
				lote: nombreDeLote(l.lote_campo_id),
				// La muestra dice `descarte`; el pallet que lo recibe es el D. Un cambio de letra de este
				// teléfono que el catálogo todavía no refleja manda (v6, §12.5).
				letra: (app.cambiosDeLetra.get(l.muestra_id)?.letra ?? l.letra) === 'descarte' ? 'D' : (app.cambiosDeLetra.get(l.muestra_id)?.letra ?? l.letra),
				muestra_id: app.cambiosDeLetra.get(l.muestra_id)?.muestra_id ?? l.muestra_id,
				campania_id: l.campania_id,
				falta_humedad: l.falta_humedad === 1,
				neto_g: l.neto_g,
				envasado_g: l.envasado_g + (local?.gramos ?? 0),
				formato_previo: l.formato_previo ?? local?.formato_bolsa_g ?? null,
				cerrado_en: l.cerrado_en,
			};
		})
		.sort((a, b) => a.cerrado_en.localeCompare(b.cerrado_en));
}

/** El pallet en armado de esa letra y ese formato, si hay: uno a la vez (Agata, 14/9). */
export function palletAbiertoPara(letra: string | null, formatoG: number): PalletProyectado | undefined {
	return letra === null ? undefined : palletsAbiertos().find((p) => p.es_tarima === 0 && p.letra === letra && p.formato_bolsa_g === formatoG);
}

/**
 * A qué pallet va el precinto, dicho antes de las bolsas (Agata, 15/10/2026; §12.18): «→ pallet Nº 241», «→ Nº 241
 * (8,25) · Nº 245 (7,25)» si todavía no tiene formato y hay uno de cada uno, o «→ pallet nuevo». Con un formato solo,
 * se dice cuál, porque el chip de las bolsas viene premarcado en 8,25. Null para un precinto sin letra. El paso del
 * pallet sigue decidiendo: esto solo lo anticipa.
 */
export function destinoDelPrecinto(p: Pick<PrecintoVisto, 'letra' | 'formato_previo'>): string | null {
	const destinos = destinosDelPrecinto(p.letra, p.formato_previo, palletsAbiertos());
	if (destinos === null) return null;
	if (destinos.length === 0) return '→ pallet nuevo';
	if (destinos.length === 1) return `→ pallet ${nombreDePallet(destinos[0])}${p.formato_previo === null ? ` · ${formatoTexto(destinos[0].formato_bolsa_g)}` : ''}`;
	return `→ ${destinos.map((d) => `${nombreDePallet(d)} (${formatoTexto(d.formato_bolsa_g).replace(' kg', '')})`).join(' · ')}`;
}

/** El número de silo de un llenado del catálogo (para envasar o para tarima), o null si ninguna de las dos listas lo trae (un precinto embolsado entero en la tarima ya no está en ellas). */
function siloDelLlenado(llenadoId: string): string | null {
	const l = (app.catalogos?.llenados_para_envasar ?? []).find((x) => x.llenado_id === llenadoId) ?? (app.catalogos?.llenados_para_tarima ?? []).find((x) => x.llenado_id === llenadoId);
	return l ? numeroDeSilo({ silo_fisico_id: l.silo_fisico_id }) : null;
}

/**
 * Los precintos cerrados SIN letra que pueden ir a una tarima de espera (§5.5 `llenados_para_tarima`),
 * menos los que ya tienen la suya —en espera acá o en el catálogo: `tarima_repetida`— y los
 * embolsados enteros, el más viejo primero. Lo ya envasado viene del catálogo (una tarima anulada
 * por metales conserva sus cargas y el servidor las sigue contando: §4.2; Codex, 15/9) más lo que
 * este teléfono cambió y el catálogo no refleja, como en los otros precintos.
 */
export function precintosParaTarima(): Array<PrecintoVisto & { cerrado_en: string }> {
	const enTarima = new Set(palletsAbiertos().filter((p) => p.es_tarima === 1).flatMap((t) => t.envasados.map((e) => e.llenado_id)));
	return (app.catalogos?.llenados_para_tarima ?? [])
		.filter((l) => !enTarima.has(l.llenado_id))
		.map((l) => {
			const local = app.saldosDeEmbolsado.get(l.llenado_id);
			return {
				llenado_id: l.llenado_id,
				precinto: l.precinto,
				silo_numero: numeroDeSilo({ silo_fisico_id: l.silo_fisico_id }),
				lote: nombreDeLote(l.lote_campo_id),
				letra: null,
				campania_id: l.campania_id,
				neto_g: l.neto_g,
				envasado_g: (l.envasado_g ?? 0) + (local?.gramos ?? 0),
				formato_previo: l.formato_previo ?? local?.formato_bolsa_g ?? null,
				cerrado_en: l.cerrado_en,
			};
		})
		.filter((p) => !precintoEntero(p))
		.sort((a, b) => a.cerrado_en.localeCompare(b.cerrado_en));
}

/** El precinto de una tarima del catálogo, para los padres de la cola (`padreDeTarima`). */
function llenadoDeTarimaDelCatalogo(palletId: string): string | null {
	return (app.catalogos?.pallets_abiertos ?? []).find((p) => p.pallet_id === palletId && p.es_tarima === 1)?.envasados[0]?.llenado_id ?? null;
}

/** La tarima de espera del precinto tal como se vio (§12.21): la base del CAS, sus bolsas de testigo y su única carga. */
export function tarimaDelPrecinto(t: PalletProyectado): TarimaDelPrecinto {
	const foto = palletVisto(t);
	const carga = t.envasados[0];
	return {
		pallet_id: t.pallet_id,
		version_id: t.version_id,
		abierto_en: t.abierto_en,
		personas: [...t.personas],
		formato_bolsa_g: t.formato_bolsa_g,
		bolsas: t.bolsas,
		envasado_id: carga?.envasado_id ?? '',
		registrado_en: carga?.registrado_en ?? '',
		persona_id: carga?.persona_id ?? '',
		ultima_carga_en: foto.ultima_carga_en,
		padre_mutacion_id: t.abierto_mutacion_id,
	};
}

/** Por qué una tarima proyectada todavía no pasa a un pallet, o null si puede (r. 17d). */
export function trabaDeLaTarima(t: PalletProyectado): string | null {
	return porQueNoPasaAPallet({ bolsas: t.bolsas, letra_de_la_muestra: t.letra_de_la_muestra, estado_del_silo: t.envasados[0]?.estado_del_silo ?? null });
}

/** La letra de la campaña abierta según el catálogo local. */
function letraDeCampania(): string | null {
	return (app.catalogos?.campanias ?? []).find((c) => c.id === campaniaAbiertaId())?.letra ?? null;
}

/**
 * La foto del pallet, congelada al elegirlo: si otro teléfono le carga mientras tanto, la revisión
 * sigue mostrando lo elegido. La campaña también se congela: `pallets_abiertos` solo trae los de la
 * campaña abierta, así que la de ahora es la del pallet, y la partida del rótulo no cambia si el
 * cierre se retoma después de un cambio de campaña.
 */
export function palletVisto(p: PalletProyectado): PalletVisto {
	const cargas = [...p.envasados, ...p.tarimas.flatMap((t) => t.envasados)];
	const horasDirectas = p.envasados.map((e) => e.registrado_en).filter((h) => h !== '');
	const horas = cargas.map((e) => e.registrado_en).filter((h) => h !== '');
	return {
		pallet_id: p.pallet_id,
		version_id: p.version_id,
		numero: p.numero,
		campania_letra: letraDeCampania(),
		letra: p.letra,
		formato_bolsa_g: p.formato_bolsa_g,
		bolsas: p.bolsas,
		precintos: precintosDe(p),
		abierto_en: p.abierto_en,
		ultima_carga_en: horasDirectas.length === 0 ? null : horasDirectas.reduce((a, b) => (a > b ? a : b)),
		primera_carga_en: horas.length === 0 ? null : horas.reduce((a, b) => (a < b ? a : b)),
		personas: [...p.personas],
		padre_mutacion_id: p.abierto_mutacion_id,
	};
}

/**
 * La tarjeta de un pallet: letra en el cuadro, número y formato, cuántos precintos y desde
 * cuándo, y a la derecha las bolsas con sus kilos. Con `apagado`, se ve pero no se elige, y
 * dice por qué (otro formato: no se mezcla).
 */
function tarjetaPallet(p: PalletProyectado, opciones: { alTocar?: () => void; verbo?: string; apagado?: string; detalle?: string } = {}): HTMLElement {
	const caja = el(opciones.alTocar || opciones.apagado ? 'button' : 'div', { class: 'tile ancho', type: 'button' });
	const letra = el('div', { class: 'cuadro' }, p.letra ?? 'T');
	letra.style.background = COLOR_LETRA[p.letra ?? ''] ?? '#59634F';
	caja.appendChild(letra);
	const precintos = precintosDe(p);
	const textos = el('span', undefined, `Pallet ${nombreDePallet(p)} · ${formatoTexto(p.formato_bolsa_g)}`);
	textos.appendChild(
		el(
			'small',
			undefined,
			opciones.apagado ??
				opciones.detalle ??
				`${precintos === 1 ? '1 precinto' : `${precintos} precintos`} · abierto ${cuandoCorto(p.abierto_en, ahoraMs())} · ${personasTexto(p.personas)}${p.local ? ' · sin subir' : ''}${p.calidad_cambio ? ' · calidad cambió' : ''}`,
		),
	);
	caja.appendChild(textos);
	const peso = el('span', { class: 'peso' });
	peso.appendChild(el('b', undefined, `${p.bolsas} bolsas`));
	peso.appendChild(el('small', undefined, `${kgConDecimales(gramosDeCarga(p.bolsas, p.formato_bolsa_g))} kg`));
	if (opciones.verbo) peso.appendChild(el('small', { class: 'verbo' }, opciones.verbo));
	caja.appendChild(peso);
	if (opciones.apagado) caja.setAttribute('disabled', '');
	else if (opciones.alTocar) (caja as HTMLButtonElement).onclick = opciones.alTocar;
	return caja;
}

/**
 * Debajo de la tarjeta, sus precintos con el total de cada uno y, debajo de cada precinto, sus cargas con quién y a qué
 * hora, y de qué tarima vino si vino de una (Agata, 15/10/2026: «¿cuántas le asignan a cada precinto para conformar el
 * palet?»; §12.18). Antes era una fila por carga, y un precinto partido en dos cargas no sumaba.
 */
function cargasDe(p: PalletProyectado | PalletCerradoProyectado, corregibles = true): HTMLElement {
	const lista = el('div', { class: 'cargas' });
	const desde = desdeParaCorregir();
	for (const precinto of cargasPorPrecinto(p)) {
		const fila = el('div', { class: 'c' });
		const izquierda = el('span', undefined, 'precinto ');
		izquierda.appendChild(el('b', undefined, precinto.precinto ?? '—'));
		fila.appendChild(izquierda);
		const derecha = el('span');
		derecha.appendChild(el('b', undefined, String(precinto.bolsas)));
		derecha.appendChild(document.createTextNode(precinto.bolsas === 1 ? ' bolsa' : ' bolsas'));
		fila.appendChild(derecha);
		lista.appendChild(fila);
		for (const c of precinto.cargas) {
			const detalle = [
				c.cant_bolsas === 1 ? '1 bolsa' : `${c.cant_bolsas} bolsas`,
				nombreDe(c.persona_id),
				...(c.registrado_en === '' ? [] : [cuandoCorto(c.registrado_en, ahoraMs())]),
				...(c.tarima === null ? [] : [`de la tarima ${nombreDeTarima(c.tarima)}`]),
				...(c.local ? ['sin subir'] : []),
			];
			// §12.18: tocar una carga la corrige, mientras no haya pasado a la oficina (48 horas, remito, mezcla).
			const delPallet = (c.tarima === null ? p.envasados : c.tarima.envasados).find((x) => x.envasado_id === c.envasado_id);
			const vista = delPallet === undefined ? null : cargaVista(p, delPallet, c.tarima);
			const traba = vista === null ? 'sin datos' : porQueNoSeCorrige(vista, desde);
			if (corregibles && vista !== null && traba === null) {
				const boton = el('button', { class: 'c carga', type: 'button' });
				boton.appendChild(el('span', undefined, detalle.join(' · ')));
				boton.appendChild(el('span', { class: 'verbo' }, 'corregir ›'));
				boton.onclick = () => acciones.corregirCarga(vista);
				lista.appendChild(boton);
			} else {
				const vieja = corregibles && vista !== null && vista.llego !== null && desde !== null && vista.llego < desde;
				lista.appendChild(el('div', { class: 'c carga' }, [...detalle, ...(vieja ? ['más de 48 h: la oficina'] : [])].join(' · ')));
			}
		}
	}
	return lista;
}

/**
 * El borde de las 48 horas para corregir desde el teléfono (§12.18): el del catálogo de cerrados, o la hora del snapshot
 * menos 48 horas; null si no se sabe, y entonces decide el servidor. Nunca el reloj del teléfono (§12.11).
 */
export function desdeParaCorregir(): string | null {
	const delCatalogo = app.catalogos?.pallets_cerrados_recientes?.desde;
	if (delCatalogo) return delCatalogo;
	const hora = app.preferencias.catalogos_hora_servidor;
	return hora ? new Date(Date.parse(hora) - HORAS_PARA_CORREGIR * HORA_MS).toISOString() : null;
}

/** La carga tal como se ve al tocarla (§12.18): su versión, su pallet efectivo —el destino, si vino de una tarima— y lo que hace falta para decir cómo queda. */
export function cargaVista(p: PalletProyectado | PalletCerradoProyectado, e: EnvasadoServidor, tarima: TarimaUnidaServidor | null): CargaVista {
	const cerrado = 'estado' in p ? p : null;
	return {
		envasado_id: e.envasado_id,
		raiz_id: e.raiz_id,
		llenado_id: e.llenado_id,
		precinto: e.precinto,
		cant_bolsas: e.cant_bolsas,
		persona_id: e.persona_id,
		registrado_en: e.registrado_en,
		formato_bolsa_g: p.formato_bolsa_g,
		pallet_id: p.pallet_id,
		pallet_directo_id: tarima?.pallet_id ?? p.pallet_id,
		pallet_numero: numeroDePallet(p),
		pallet_estado: cerrado !== null ? cerrado.estado : p.es_tarima === 1 ? 'espera' : 'abierto',
		pallet_bolsas: p.bolsas,
		pallet_cargas: p.envasados.length + p.tarimas.reduce((n, t) => n + t.envasados.length, 0),
		despachado: cerrado !== null && cerrado.despachado === 1,
		llego: e.llego ?? null,
		padre_mutacion_id: e.mutacion_id ?? null,
		// §12.21: la carga de una parte, o la de una tarima en espera con bolsas repartidas.
		de_un_pase: tarima?.es_parte === 1 || (p.es_tarima === 1 && p.tiene_partes === 1),
	};
}

/** La carga de AHORA con la raíz de la vista: en un pallet en armado o entre los cerrados; null si ya no está (se anuló, o su pallet se tiró). */
function cargaAlDia(c: CargaVista): CargaVista | null {
	for (const p of [...palletsAbiertos(), ...app.palletsCerrados]) {
		const directa = p.envasados.find((e) => e.raiz_id === c.raiz_id || e.envasado_id === c.envasado_id);
		if (directa) return cargaVista(p, directa, null);
		for (const t of p.tarimas) {
			const deTarima = t.envasados.find((e) => e.raiz_id === c.raiz_id || e.envasado_id === c.envasado_id);
			if (deTarima) return cargaVista(p, deTarima, t);
		}
	}
	return null;
}

/** El precinto de la carga, con lo que este teléfono cargó y el catálogo no refleja: de ahí sale el tope al corregir; null si no está en las listas (decide el servidor). */
function precintoDeLaCarga(c: CargaVista): PrecintoVisto | null {
	return precintosParaEmbolsar().find((x) => x.llenado_id === c.llenado_id) ?? precintosParaTarima().find((x) => x.llenado_id === c.llenado_id) ?? null;
}

/** «pallet Nº 241» o «pallet Nº 241 (cerrado)», para los textos de la carga. */
function palletDeLaCargaTexto(c: CargaVista): string {
	return `pallet ${nombreDePallet({ pallet_id: c.pallet_id, numero: c.pallet_numero })}${c.pallet_estado === 'cerrado' ? ' (cerrado)' : ''}`;
}

// ── Los cerrados de las últimas 48 horas (§12.18) ─────────────────────────

/** Los cerrados de toda la estación, con lo que puso cada precinto; los que salieron o entraron a una mezcla se ven, pero los corrige la oficina. */
export function pintarCerradosEnvasado(cuerpo: HTMLElement, pieNodo: HTMLElement): void {
	cuerpo.appendChild(el('div', { class: 'q arriba' }, 'Cerrados · últimas 48 h'));
	cuerpo.appendChild(el('div', { class: 'qsub' }, 'De todos los teléfonos de envasado. Una carga se corrige hasta 48 horas después de cargada, aunque el pallet esté cerrado.'));
	const grilla = el('div', { class: 'grid1' });
	for (const p of app.palletsCerrados) {
		const traba = p.despachado === 1 ? 'Salió con remito: lo corrige la oficina.' : p.estado === 'consumido' ? 'Entró a una mezcla: lo corrige la oficina.' : null;
		const precintos = precintosDe(p);
		const cerrado = p.cerrado_en === '' ? '' : `cerrado ${cuandoCorto(p.cerrado_en, ahoraMs())} · `;
		grilla.appendChild(tarjetaPallet(p, { detalle: `${cerrado}${precintos === 1 ? '1 precinto' : `${precintos} precintos`}${p.local ? ' · sin subir' : ''}` }));
		if (p.envasados.length + p.tarimas.length > 0) grilla.appendChild(cargasDe(p, traba === null));
		if (traba !== null) grilla.appendChild(el('div', { class: 'por-que' }, traba));
	}
	if (app.palletsCerrados.length === 0) cuerpo.appendChild(el('div', { class: 'qsub' }, 'No hay pallets cerrados en las últimas 48 horas.'));
	cuerpo.appendChild(grilla);
	const volver = el('button', { class: 'btn go ancho', type: 'button' }, 'Volver al inicio');
	volver.onclick = () => acciones.inicioEnvasado();
	pieNodo.appendChild(volver);
}

/** Lo que una tarjeta o una fila dicen de un precinto, en el mismo orden: silo, lote, neto, humedad, notas; los días que lleva; el saldo. */
function detalleDePrecinto(p: PrecintoVisto & { cerrado_en?: string }): { basicas: string[]; dias: string | null; tarde: boolean; saldo: string | null } {
	const diasN = p.cerrado_en === undefined ? null : diasDesde(p.cerrado_en, ahoraMs());
	const saldoN = saldoG(p);
	const basicas = [`silo ${p.silo_numero}`, p.lote, p.neto_g === null ? 'sin pesar' : `${kg(p.neto_g)} kg netos`];
	// v6 (§12.9): sin humedad la letra es provisoria; se embolsa igual y calidad la completa.
	if (p.falta_humedad) basicas.push('falta humedad');
	const notas = cuantasNotas(p.llenado_id).replace(' · ', '');
	if (notas) basicas.push(notas);
	// La alerta de los cinco días (5/9) vive en la lista, no en un cartel aparte.
	const tarde = diasN !== null && diasN >= DIAS_DE_ALERTA;
	const dias = diasN === null ? null : tarde ? `⚠ hace ${diasN} días` : diasN === 0 ? 'hoy' : diasN === 1 ? 'hace 1 día' : `hace ${diasN} días`;
	const saldo = p.envasado_g > 0 ? (precintoEntero(p) ? 'embolsado entero' : saldoN === null ? `ya ${kgConDecimales(p.envasado_g)} kg envasados` : `quedan ${kgConDecimales(Math.max(0, saldoN))} kg`) : null;
	// §12.21: las bolsas en la tarima de espera son del precinto, sin pallet todavía: embolsarlo las reparte.
	const enTarima = p.en_tarima ?? null;
	if (enTarima !== null) {
		const espera = `${enTarima.bolsas} ${enTarima.bolsas === 1 ? 'bolsa embolsada' : 'bolsas embolsadas'} en espera, sin pallet`;
		return { basicas, dias, tarde, saldo: saldoN === null || precintoEntero(p) ? espera : `${espera} · quedan ${kgConDecimales(Math.max(0, saldoN))} kg` };
	}
	return { basicas, dias, tarde, saldo };
}

function tarjetaPrecinto(p: PrecintoVisto & { cerrado_en?: string }, alTocar: () => void, seleccionado: boolean): HTMLElement {
	const { basicas, dias, saldo } = detalleDePrecinto(p);
	return tile({
		etiqueta: `precinto ${p.precinto}`,
		sub: [...basicas, ...(dias === null ? [] : [dias]), ...(saldo === null ? [] : [saldo])].join(' · '),
		destacado: destinoDelPrecinto(p) ?? undefined,
		color: COLOR_LETRA[p.letra ?? ''] ?? PALETA[0],
		avatarCuadrado: true,
		avatarTexto: p.letra ?? '?',
		seleccionado,
		alTocar,
	});
}

/**
 * La fila compacta del inicio (boceto: la «tarjeta chica» de «Con letra, sin embolsar»): la letra en un
 * cuadro chico, el precinto con su detalle, y a la derecha los días que lleva, en ámbar desde los
 * cinco. Una fila por precinto, no un tile: con cinco esperando, los tiles hacían del inicio más de dos
 * pantallas y la alerta quedaba bajo el pliegue (2/10). Tocarla es embolsarlo.
 */
function filaPrecinto(p: PrecintoVisto & { cerrado_en: string }, alTocar: () => void): HTMLButtonElement {
	const { basicas, dias, tarde, saldo } = detalleDePrecinto(p);
	// Dos renglones (CSS grid): la letra a la izquierda, el precinto con los días a la derecha, y el detalle
	// debajo a todo lo ancho, para que entre en una línea en un teléfono angosto.
	const fila = el('button', { class: 'fila-precinto', type: 'button' });
	const cuadro = el('div', { class: 'cuadro' }, p.letra ?? '?');
	cuadro.style.background = COLOR_LETRA[p.letra ?? ''] ?? PALETA[0];
	fila.appendChild(cuadro);
	fila.appendChild(el('b', undefined, `precinto ${p.precinto}`));
	fila.appendChild(el('span', { class: tarde ? 'dias tarde' : 'dias' }, dias ?? ''));
	fila.appendChild(el('small', undefined, [...basicas, ...(saldo === null ? [] : [saldo])].join(' · ')));
	const destino = destinoDelPrecinto(p);
	if (destino !== null) fila.appendChild(el('span', { class: 'destino' }, destino));
	fila.onclick = alTocar;
	return fila;
}

/**
 * La tarjeta de una tarima de espera (boceto, inicio): «T» en el cuadro, su precinto y si la letra
 * llegó; a la derecha las bolsas. Con letra, tocarla es pasarla a un pallet («pasar a pallet ›»).
 */
function tarjetaTarima(t: PalletProyectado, opciones: { alTocar?: () => void; seleccionado?: boolean } = {}): HTMLElement {
	const primera = t.envasados[0];
	const tocable = opciones.alTocar !== undefined;
	const caja = el(tocable ? 'button' : 'div', { class: 'tile ancho', type: 'button' });
	const cuadro = el('div', { class: 'cuadro' }, 'T');
	cuadro.style.background = '#59634F';
	caja.appendChild(cuadro);
	const textos = el('span', undefined, `Tarima ${nombreDeTarima(t)}`);
	// El silo solo si alguna lista lo trae: «silo —» no dice nada (2/10).
	const silo = primera ? siloDelLlenado(primera.llenado_id) : null;
	const partes = [`precinto ${primera?.precinto ?? '—'}`, ...(silo === null ? [] : [`silo ${silo}`]), formatoTexto(t.formato_bolsa_g)];
	const traba = trabaDeLaTarima(t);
	// Con la letra, el precinto está en la lista con sus bolsas en espera (§12.21): la tarjeta solo vive mientras haya traba.
	partes.push(traba ?? `la letra llegó: ${t.letra_de_la_muestra === 'descarte' ? 'descarte (va a un D)' : t.letra_de_la_muestra} · se embolsa desde la lista`);
	if (t.tiene_partes === 1) partes.push('parte ya pasó a un pallet');
	if (t.local) partes.push('sin subir');
	textos.appendChild(el('small', undefined, partes.join(' · ')));
	caja.appendChild(textos);
	const peso = el('span', { class: 'peso' });
	peso.appendChild(el('b', undefined, `${t.bolsas} bolsas`));
	peso.appendChild(el('small', undefined, `${kgConDecimales(gramosDeCarga(t.bolsas, t.formato_bolsa_g))} kg`));
	caja.appendChild(peso);
	if (opciones.seleccionado !== undefined) caja.setAttribute('aria-pressed', opciones.seleccionado ? 'true' : 'false');
	if (opciones.alTocar) (caja as HTMLButtonElement).onclick = opciones.alTocar;
	return caja;
}

// ── El inicio de envasado ─────────────────────────────────────────────────

/**
 * Los pallets en armado son la pantalla (boceto §3): la tarjeta con su número, letra,
 * formato, precintos y bolsas, y debajo sus precintos. Tocar la tarjeta es cerrarlo.
 * Abajo, «Embolsar un precinto», las acciones, y los precintos con letra que esperan,
 * el más viejo arriba y con los días que llevan; tocar uno es embolsarlo.
 */
export function pintarInicioEnvasado(cuerpo: HTMLElement, pieNodo: HTMLElement, aviso: string | null): void {
	const pallets = palletsAbiertos().filter((p) => p.es_tarima === 0);
	cuerpo.appendChild(el('div', { class: 'q arriba' }, `Turno ${NOMBRE_TURNO[turnoDe(ahoraMs())]}`));
	cuerpo.appendChild(el('div', { class: 'qsub' }, pallets.length === 0 ? 'No hay pallet en armado.' : pallets.length === 1 ? '1 pallet en armado' : `${pallets.length} pallets en armado`));
	const grilla = el('div', { class: 'grid1' });
	for (const p of pallets) {
		// Tocar la tarjeta es cerrarlo; uno sin bolsas no se cierra (la revisión lo bloqueaba): se tira.
		grilla.appendChild(tarjetaPallet(p, p.bolsas > 0 ? { alTocar: () => acciones.cerrarPallet(p), verbo: 'cerrar ›' } : {}));
		if (p.envasados.length + p.tarimas.length > 0) grilla.appendChild(cargasDe(p));
	}
	// Las tarimas de espera, debajo, solo mientras algo las trabe (sin letra; el silo anulado, descartado o reabierto): con
	// la letra, el precinto vuelve a la lista con sus bolsas en espera y embolsarlo las reparte (§12.21). Debajo, sus cargas:
	// un dedazo en una tarima se corrige desde acá.
	const tarimas = palletsAbiertos().filter((p) => p.es_tarima === 1);
	for (const t of tarimas.filter((t) => trabaDeLaTarima(t) !== null)) {
		grilla.appendChild(tarjetaTarima(t));
		if (t.envasados.length > 0) grilla.appendChild(cargasDe(t));
	}
	cuerpo.appendChild(grilla);
	// §12.18: los cerrados de las últimas 48 horas, de toda la estación, con lo que puso cada precinto.
	if (app.palletsCerrados.length > 0) {
		const cerrados = el('button', { class: 'enlace', type: 'button' }, `Cerrados · últimas 48 h · ${app.palletsCerrados.length} ›`);
		cerrados.onclick = () => acciones.verCerrados();
		cuerpo.appendChild(cerrados);
	}

	const embolsar = el('button', { class: 'btn go ancho', type: 'button' }, 'Embolsar un precinto');
	embolsar.onclick = () => acciones.embolsar();
	cuerpo.appendChild(embolsar);
	if (aviso) cuerpo.appendChild(flag('warn', aviso, ''));

	// Dos acciones sobre lo que hay en armado. «Pallet nuevo» ya no es una acción aparte: el pallet nace
	// con su primera carga, adentro de embolsar (Nicolás, 2/10). Se puede cerrar uno con bolsas; uno
	// vacío (gestión le anuló las cargas) solo se tira.
	const fila = el('div', { class: 'grid2 acciones' });
	const conBolsas = pallets.filter((p) => p.bolsas > 0);
	const nadaEnArmado = pallets.length === 0 && tarimas.length === 0;
	const botones: Array<[string, string, boolean, () => void]> = [
		['Cerrar pallet', conBolsas.length === 0 ? 'no hay pallet con bolsas' : 'arma el rótulo', conBolsas.length === 0, () => acciones.cerrarPallet()],
		['Se tira un pallet', nadaEnArmado ? 'no hay nada en armado' : 'metales u otro', nadaEnArmado, () => acciones.tirarPallet()],
	];
	for (const [texto, sub, deshabilitado, alTocar] of botones) {
		const boton = el('button', { class: 'btn accion', type: 'button' }, texto);
		boton.appendChild(el('small', undefined, sub));
		if (deshabilitado) boton.setAttribute('disabled', '');
		boton.onclick = alTocar;
		fila.appendChild(boton);
	}
	cuerpo.appendChild(fila);
	// La excepción, aparte y rara (docs/embolsado.md §3; boceto): un precinto sin letra porque faltó calidad.
	const tarima = el('button', { class: 'enlace', type: 'button' }, 'Tarima de espera · un precinto sin letra');
	tarima.onclick = () => acciones.tarimaDeEspera();
	cuerpo.appendChild(tarima);

	const esperan = precintosParaElegir();
	cuerpo.appendChild(el('div', { class: 'qsub despues' }, `Con letra, sin embolsar · ${esperan.length}`));
	const lista = el('div', { class: 'lista-precintos' });
	for (const p of esperan) lista.appendChild(filaPrecinto(p, () => acciones.embolsar(p)));
	cuerpo.appendChild(lista);

	const ver = el('button', { class: 'btn ghost ancho', type: 'button' }, 'Ver lo cargado');
	ver.onclick = () => acciones.verLoCargado();
	pieNodo.appendChild(ver);
}

// ── El flujo ──────────────────────────────────────────────────────────────

export function pintarEnvasado(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorEmbolsado): void {
	const visibles = indicesDe(b);
	const puntos = el('div', { class: 'dots' });
	const hechos = visibles.filter((i) => i <= b.paso).length;
	for (let i = 0; i < visibles.length; i++) {
		const punto = el('i');
		if (i < hechos) punto.className = 'on';
		puntos.appendChild(punto);
	}
	cuerpo.appendChild(puntos);
	const contenido = el('div', { class: 'appbody' });
	cuerpo.appendChild(contenido);
	if (avisoDeRevision?.capture_id === b.capture_id && avisoDeRevision.paso === b.paso) contenido.appendChild(flag('warn', avisoDeRevision.texto, ''));
	switch (nombrePaso(b)) {
		case 'quien':
			return pQuien(contenido, pieNodo, b);
		case 'precinto':
			return pPrecinto(contenido, pieNodo, b as BorradorEmbolsar);
		case 'bolsas':
			return pBolsas(contenido, pieNodo, b as BorradorEmbolsar);
		case 'pallet':
			return pPallet(contenido, pieNodo, b as BorradorEmbolsar);
		case 'personas':
			return pPersonas(contenido, pieNodo, b as BorradorEmbolsar);
		case 'rotulo':
			return pRotulo(contenido, pieNodo, b as ConRotulo);
		case 'cual':
			return pCual(contenido, pieNodo, b as BorradorCerrarPallet | BorradorAnularPallet);
		case 'motivo':
			return pMotivo(contenido, pieNodo, b as BorradorAnularPallet);
		case 'nueva_letra':
			return pNuevaLetra(contenido, pieNodo, b as BorradorCambiarLetra);
		case 'motivo_letra':
			return pMotivoLetra(contenido, pieNodo, b as BorradorCambiarLetra);
		case 'texto_nota':
			return pTextoNota(contenido, pieNodo, b as BorradorNotaPrecinto);
		case 'bolsas_carga':
			return pBolsasCarga(contenido, pieNodo, b as BorradorCorregirCarga);
		case 'motivo_carga':
			return pMotivoCarga(contenido, pieNodo, b as BorradorAnularCarga);
		case 'hora':
			return pHora(contenido, pieNodo, b);
		default:
			return pRevisar(contenido, pieNodo, b);
	}
}

// ── v6: la letra final y las notas del precinto (§12.5, §12.4) ────────────

// Los mismos nombres que calidad y el pallet (2/10); el descarte dice a dónde va.
const NOMBRE_LETRA_MUESTRA: Record<string, string> = { ...NOMBRE_LETRA, descarte: 'Descarte · va a un D' };

/** La letra de la muestra tal como la dice la lista de envasar (D por descarte), vuelta a la de la muestra. */
function letraDeMuestraDe(letra: string | null): BorradorCambiarLetra['letra_actual'] {
	if (letra === 'D') return 'descarte';
	return letra === 'Q' || letra === 'P' || letra === 'L' ? letra : null;
}
const COLOR_LETRA_MUESTRA: Record<string, string> = { Q: COLOR_LETRA.Q, P: COLOR_LETRA.P, L: COLOR_LETRA.L, descarte: COLOR_LETRA.D };

function pNuevaLetra(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorCambiarLetra): void {
	pregunta(cuerpo, `¿Qué calidad tiene el precinto ${b.precinto ?? ''}?`, `Hoy es ${b.letra_actual === null ? 'sin letra' : NOMBRE_LETRA_MUESTRA[b.letra_actual]}. Lo que cambiás acá vale para todo el precinto.`);
	listaDeNotas(cuerpo, b.llenado_id, b.precinto);
	const grilla = el('div', { class: 'grid2' });
	for (const letra of LETRAS_MUESTRA) {
		const actual = letra === b.letra_actual;
		grilla.appendChild(
			tile({
				etiqueta: NOMBRE_LETRA_MUESTRA[letra],
				...(actual ? { badge: 'la de hoy' } : {}),
				color: COLOR_LETRA_MUESTRA[letra],
				avatarCuadrado: true,
				avatarTexto: letra === 'descarte' ? 'D' : letra,
				seleccionado: b.letra === letra,
				deshabilitado: actual,
				alTocar: () => {
					b.letra = letra;
					guardarYPintar(b);
				},
			}),
		);
	}
	cuerpo.appendChild(grilla);
	pie(pieNodo, b, { texto: 'Siguiente', deshabilitado: b.letra === null, alTocar: () => void avanzar(b) });
}

function pMotivoLetra(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorCambiarLetra): void {
	pregunta(cuerpo, '¿Por qué cambia?', 'Obligatorio. Lo ven la analista y la oficina.');
	cajaDeTexto(cuerpo, () => b.motivo, (v) => (b.motivo = v), 'Ej.: se vieron palitos al embolsar', () => void guardarBorrador(b));
	pie(pieNodo, b, { texto: 'Siguiente', alTocar: () => void avanzar(b) });
}

function pTextoNota(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorNotaPrecinto): void {
	pregunta(cuerpo, `Nota al precinto ${b.precinto ?? ''}`, 'La ven producción, calidad y envasado cuando miran este precinto.');
	listaDeNotas(cuerpo, b.llenado_id, b.precinto);
	cajaDeTexto(cuerpo, () => b.texto, (v) => (b.texto = v), 'Ej.: olor raro en las primeras bolsas', () => void guardarBorrador(b));
	pie(pieNodo, b, { texto: 'Siguiente', alTocar: () => void avanzar(b) });
}

/**
 * Debajo del precinto elegido: sus notas y lo que se puede hacer con él además de embolsarlo. Son
 * desvíos: el embolsado desde el que se entra (`desde`) se descarta —nació segundos antes; a lo sumo
 * se pierden dos dígitos— y la pantalla «listo» del desvío ofrece volver a embolsarlo con la letra ya
 * actualizada. Si quedara guardado, reaparecía como «¿seguís con…?» con la letra vieja y «¿A qué
 * pallet?» ofrecía abrir un pallet de esa letra (Nicolás, 2/10: un registro a medias por vez).
 */
function accionesDelPrecinto(cuerpo: HTMLElement, p: PrecintoVisto, desde: BorradorEmbolsar | null = null): void {
	listaDeNotas(cuerpo, p.llenado_id, p.precinto);
	const fila = el('div', { class: 'acciones-precinto' });
	const desviar = async (ir: () => void) => {
		if (desde !== null) await descartarBorrador(desde).catch(() => null);
		ir();
	};
	if (p.letra !== null && p.muestra_id) {
		const letra = el('button', { class: 'enlace', type: 'button' }, 'Cambiar la calidad');
		letra.onclick = () => void desviar(() => acciones.cambiarLetra(p));
		fila.appendChild(letra);
	}
	const nota = el('button', { class: 'enlace', type: 'button' }, 'Nota al precinto');
	nota.onclick = () => void desviar(() => acciones.notaPrecinto(p));
	fila.appendChild(nota);
	cuerpo.appendChild(fila);
}

function pQuien(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorEmbolsado): void {
	// Solo el muleto llega acá: en un teléfono personal el «quién» viene del enrolamiento.
	pregunta(
		cuerpo,
		b.tipo === 'cerrar_pallet'
			? '¿Quién cierra el pallet?'
			: b.tipo === 'anular_pallet'
				? '¿Quién tira el pallet?'
				: b.tipo === 'cambiar_letra'
					? '¿Quién cambia la calidad?'
					: b.tipo === 'nota_precinto'
						? '¿Quién escribe la nota?'
						: b.tipo === 'corregir_carga'
							? '¿Quién corrige?'
							: b.tipo === 'anular_carga'
								? '¿Quién anula la carga?'
								: '¿Quién sos?',
		'Tocá tu nombre.',
	);
	cuerpo.appendChild(grillaPersonas(b.personas, 1, () => guardarYPintar(b), 'envasado'));
	pie(pieNodo, b, { texto: 'Siguiente', deshabilitado: b.personas.length === 0, alTocar: () => void avanzar(b) });
}

/** Elegir el precinto congela lo que se vio de él y propone el formato: el que ya tiene, o 8,25. */
export function elegirPrecinto(b: BorradorEmbolsar, p: PrecintoVisto): void {
	const enTarima = p.en_tarima ?? null;
	b.precinto = {
		llenado_id: p.llenado_id,
		precinto: p.precinto,
		silo_numero: p.silo_numero,
		lote: p.lote,
		letra: p.letra,
		neto_g: p.neto_g,
		envasado_g: p.envasado_g,
		formato_previo: p.formato_previo,
		...(p.campania_id === undefined ? {} : { campania_id: p.campania_id }),
		...(p.muestra_id === undefined ? {} : { muestra_id: p.muestra_id }),
		...(p.falta_humedad === undefined ? {} : { falta_humedad: p.falta_humedad }),
		en_tarima: enTarima === null ? null : { ...enTarima, personas: [...enTarima.personas] },
	};
	b.formato_bolsa_g = p.formato_previo ?? FORMATO_PREMARCADO;
	// Cambiar de precinto cambia de letra, y con ella el pallet que corresponde.
	b.destino = null;
	// La tarima de espera nace con esta carga, con identidad propia y sin rótulo.
	b.nuevo = b.tarima ? { pallet_id: generarUlid(), version_id: generarUlid(), mutacion_id: generarUlid(), rotulo: null } : null;
	// §12.21: con el precinto en una tarima, la identidad del pase nace acá y se conserva entre pantallas.
	b.reparto = enTarima === null ? null : nuevoReparto();
}

function nuevoReparto(): NonNullable<BorradorEmbolsar['reparto']> {
	return { version_id: generarUlid(), carga_version_id: generarUlid(), mutacion_id: generarUlid(), parte: { pallet_id: generarUlid(), version_id: generarUlid(), envasado_id: generarUlid() } };
}

/** Otro formato es otro pallet: lo elegido antes ya no vale. La tarima conserva su identidad —no tiene pallet que reelegir— (Codex, 15/9). */
export function elegirFormato(b: BorradorEmbolsar, formato: number): void {
	b.formato_bolsa_g = formato;
	b.destino = null;
	if (!b.tarima) b.nuevo = null;
}

/** «No tiene letra todavía: tarima de espera» (y la vuelta): cambia de lista y el precinto se vuelve a elegir. */
export function irATarima(b: BorradorEmbolsar, tarima: boolean): void {
	b.tarima = tarima;
	b.precinto = null;
	b.formato_bolsa_g = null;
	b.destino = null;
	b.nuevo = null;
	b.personas_pallet = [];
	b.reparto = null;
}

/** El tope de carga, dicho: tres bolsas del formato o el 10 %, lo que sea mayor (§12.14). */
const BOLSAS_DEL_TOPE = String(BOLSAS_DE_TOLERANCIA);
const POR_CIENTO_DEL_TOPE = String(Math.round(TOLERANCIA_SOBRE_NETO * 100));

/** Los precintos que se pueden elegir: con letra y sin embolsar entero (Agata, 15/9: «cuando se envasó un precinto, que desaparezca»; 7/10: con menos de tres bolsas de saldo). */
export function precintosParaElegir(): Array<PrecintoVisto & { cerrado_en: string }> {
	// §12.21: uno con bolsas en una tarima de espera no se esconde aunque esté entero: hay que repartirlas.
	return precintosParaEmbolsar().filter((p) => !precintoEntero(p) || p.en_tarima);
}

/**
 * Los embolsados enteros a los que todavía les entra una bolsa dentro del tope (tres bolsas o el 10 %):
 * el remanente de la línea se anota desde acá. **El último cerrado arriba**, al revés de la lista
 * habitual: el remanente es del precinto que se acaba de terminar. En producción son todos los de la
 * campaña (693 el 19/10/2026: un precinto embolsado dentro del margen queda «entero con remanente» para
 * siempre), y con el más viejo arriba el 177144 recién partido quedaba último y nadie lo encontraba
 * («el precinto desapareció», Agata, 19/10; `docs/esquema.md` §12.14).
 */
export function precintosConRemanente(): Array<PrecintoVisto & { cerrado_en: string }> {
	return precintosParaEmbolsar()
		.filter((p) => admiteRemanente(p) && !p.en_tarima)
		.sort((a, b) => b.cerrado_en.localeCompare(a.cerrado_en) || b.llenado_id.localeCompare(a.llenado_id));
}

/** Cuántos embolsados enteros se ven al abrir la lista: los últimos cerrados; los demás, a pedido. */
export const ENTEROS_A_LA_VISTA = 10;

/**
 * Los embolsados enteros que se pintan: los `ENTEROS_A_LA_VISTA` últimos, o todos si se pidieron o si
 * el precinto elegido quedó más abajo (la tarjeta elegida nunca se esconde: debajo van sus acciones).
 */
export function enterosALaVista<T extends { llenado_id: string }>(enteros: T[], elegidoId: string | null, todos: boolean): T[] {
	if (todos || enteros.length <= ENTEROS_A_LA_VISTA) return enteros;
	const elegido = elegidoId === null ? -1 : enteros.findIndex((p) => p.llenado_id === elegidoId);
	return elegido >= ENTEROS_A_LA_VISTA ? enteros : enteros.slice(0, ENTEROS_A_LA_VISTA);
}

/**
 * La vista de los enteros al elegir un precinto: se pliegan, salvo que el elegido sea uno de ellos —si no,
 * la tarjeta tocada desaparecía y nada mostraba qué quedó elegido (visto en el pane, 2/10)—, y si se habían
 * pedido todos, siguen todos.
 */
export function vistaAlElegir(vista: VistaDeEnteros, elegidoEsEntero: boolean): VistaDeEnteros {
	return !elegidoEsEntero ? 'plegados' : vista === 'plegados' ? 'ultimos' : vista;
}

/** Lo que cambió del precinto desde que se eligió, dicho para la pantalla, y el paso al que vuelve. */
export interface CambioDelPrecinto {
	texto: string;
	paso: 'bolsas' | 'pallet';
}

/**
 * Lo elegido del precinto se congela en su identidad, pero el neto, lo envasado, el formato previo y
 * la letra son los de AHORA (el catálogo con la cola encima): producción pudo corregir el neto, otro
 * teléfono pudo cargarle o embolsarlo con el otro formato mientras esta carga se armaba, y un
 * borrador bloqueado con un neto viejo no tenía cómo destrabarse (Codex, ronda del diff de la
 * tolerancia, 15/9). Si el formato quedó fijado en otro, la carga pasa a ese y el pallet se vuelve
 * a elegir. Si la letra cambió —calidad, la oficina, o este mismo teléfono con «Cambiar la calidad»
 * (v6, §12.5)—, el precinto pasa a su letra nueva y el pallet se vuelve a elegir: antes la letra
 * quedaba congelada hasta la confirmación y «¿A qué pallet?» ofrecía abrir uno de la letra vieja
 * (2/10). Devuelve el aviso y a qué paso vuelve, o null si nada cambió.
 */
export function precintoAlDia(b: BorradorEmbolsar): CambioDelPrecinto | null {
	const p = b.precinto;
	if (p === null) return null;
	const ahora = (b.tarima ? precintosParaTarima() : precintosParaEmbolsar()).find((x) => x.llenado_id === p.llenado_id);
	if (!ahora) return null;
	p.neto_g = ahora.neto_g;
	p.envasado_g = ahora.envasado_g;
	p.formato_previo = ahora.formato_previo;
	if (ahora.muestra_id !== undefined) p.muestra_id = ahora.muestra_id;
	if (ahora.falta_humedad !== undefined) p.falta_humedad = ahora.falta_humedad;
	if (ahora.letra !== p.letra) {
		const anterior = p.letra ?? '—';
		p.letra = ahora.letra;
		b.destino = null;
		if (!b.tarima) b.nuevo = null;
		return { texto: `La letra del precinto cambió: era ${anterior} y ahora es ${ahora.letra ?? '—'}. El pallet se elige de nuevo.`, paso: 'pallet' };
	}
	// §12.21: la tarima de espera del precinto es la de AHORA: otro teléfono pudo pasarla entera o de a partes, la oficina
	// pudo tirarla, corregirla o deshacer un pase; con la foto vieja la unión caía en el CAS (`composicion_cambio`).
	const tarimaAntes = p.en_tarima ?? null;
	const tarimaAhora = b.tarima ? null : (ahora.en_tarima ?? null);
	const cambioLaTarima =
		(tarimaAntes === null) !== (tarimaAhora === null) ||
		(tarimaAntes !== null &&
			tarimaAhora !== null &&
			(tarimaAntes.pallet_id !== tarimaAhora.pallet_id || tarimaAntes.version_id !== tarimaAhora.version_id || tarimaAntes.bolsas !== tarimaAhora.bolsas || tarimaAntes.ultima_carga_en !== tarimaAhora.ultima_carga_en));
	if (cambioLaTarima) {
		p.en_tarima = tarimaAhora;
		if (tarimaAhora === null) b.reparto = null;
		else b.reparto ??= nuevoReparto();
		return {
			texto:
				tarimaAhora === null
					? 'La tarima de este precinto ya pasó a un pallet desde otro teléfono, o la oficina la tiró: las bolsas que cargues son nuevas del silo.'
					: tarimaAntes === null
						? `Este precinto tiene ahora ${tarimaAhora.bolsas} bolsas en una tarima de espera: las primeras que cargues salen de ahí.`
						: `La tarima tiene ahora ${tarimaAhora.bolsas} bolsas: revisá cuántas pasan.`,
			paso: 'bolsas',
		};
	}
	if (p.formato_previo === null || b.formato_bolsa_g === null || b.formato_bolsa_g === p.formato_previo) return null;
	elegirFormato(b, p.formato_previo);
	return {
		texto: b.tarima
			? `Este precinto ya se embolsó con ${formatoTexto(p.formato_previo)}: la tarima sigue con ese formato.`
			: `Mientras tanto este precinto se embolsó con ${formatoTexto(p.formato_previo)}: la carga sigue con ese formato, y el pallet se elige de nuevo.`,
		paso: 'bolsas',
	};
}

function pPrecinto(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorEmbolsar): void {
	const elegir = (p: PrecintoVisto) => {
		fijarVistaDeEnteros(b, vistaAlElegir(vistaDeEnterosDe(b), precintosConRemanente().some((x) => x.llenado_id === p.llenado_id)));
		elegirPrecinto(b, p);
		guardarYPintar(b);
	};
	const cambiarDeLista = (texto: string, tarima: boolean) => {
		const enlace = el('button', { class: 'enlace', type: 'button' }, texto);
		enlace.onclick = () => {
			irATarima(b, tarima);
			guardarYPintar(b);
		};
		cuerpo.appendChild(enlace);
	};
	if (b.tarima) {
		// La tarima de espera (boceto «¿Qué precinto va a la tarima?»): solo cuando faltó la persona de calidad.
		pregunta(cuerpo, '¿Qué precinto va a la tarima?', 'Los cerrados sin muestra. Un precinto por tarima.');
		const sinLetra = precintosParaTarima();
		if (sinLetra.length === 0) cuerpo.appendChild(flag('ok', 'Todos los silos cerrados ya tienen letra.', 'No hace falta ninguna tarima.'));
		const grilla = el('div', { class: 'grid1' });
		for (const p of sinLetra) grilla.appendChild(tarjetaPrecinto(p, () => elegir(p), b.precinto?.llenado_id === p.llenado_id));
		cuerpo.appendChild(grilla);
		cambiarDeLista('Ya tiene letra: va a un pallet', false);
		pie(pieNodo, b, { texto: 'Siguiente', deshabilitado: b.precinto === null, alTocar: () => void avanzar(b) });
		return;
	}
	pregunta(cuerpo, '¿Qué precinto embolsás?', 'Los que ya tienen letra, el más viejo arriba. Sin letra no se embolsa; embolsado entero, desaparece.');
	const lista = precintosParaElegir();
	if (lista.length === 0) cuerpo.appendChild(el('div', { class: 'qsub' }, 'Ningún precinto con letra espera embolsarse.'));
	// v6 (§12.4, §12.5): las notas del precinto elegido, y cambiarle la calidad o anotarle algo, debajo
	// de SU tarjeta: al final de la lista quedaban lejos del precinto que se tocó (2/10).
	const conAcciones = (grilla: HTMLElement, p: PrecintoVisto & { cerrado_en: string }) => {
		const elegido = b.precinto?.llenado_id === p.llenado_id;
		grilla.appendChild(tarjetaPrecinto(p, () => elegir(p), elegido));
		if (elegido) {
			const caja = el('div', { class: 'del-precinto' });
			accionesDelPrecinto(caja, p, b);
			grilla.appendChild(caja);
		}
	};
	const grilla = el('div', { class: 'grid1' });
	for (const p of lista) conAcciones(grilla, p);
	cuerpo.appendChild(grilla);
	// Los embolsados enteros desaparecen (Agata, 15/9), pero un remanente de la línea se anota
	// desde acá, bajo el mismo tope (tres bolsas o el 10 %): los que no admiten ni una bolsa más no se ofrecen.
	const enteros = precintosConRemanente();
	if (enteros.length > 0) {
		const vista = vistaDeEnterosDe(b);
		const ver = el('button', { class: 'btn ghost ancho', type: 'button' }, vista !== 'plegados' ? 'Ocultar los embolsados enteros' : `Embolsados enteros · ${enteros.length}`);
		ver.onclick = () => {
			fijarVistaDeEnteros(b, vista === 'plegados' ? 'ultimos' : 'plegados');
			app.render();
		};
		cuerpo.appendChild(ver);
		if (vista !== 'plegados') {
			cuerpo.appendChild(el('div', { class: 'qsub' }, `Ya no les queda saldo. Solo si quedó un remanente en la línea: hasta ${BOLSAS_DEL_TOPE} bolsas (o el ${POR_CIENTO_DEL_TOPE} %) por encima del silo. El último cerrado, arriba.`));
			// Los últimos cerrados a la vista y el resto a pedido: son todos los de la campaña (19/10/2026).
			const aLaVista = enterosALaVista(enteros, b.precinto?.llenado_id ?? null, vista === 'todos');
			const remanentes = el('div', { class: 'grid1' });
			for (const p of aLaVista) conAcciones(remanentes, p);
			cuerpo.appendChild(remanentes);
			if (aLaVista.length < enteros.length) {
				const masViejos = el('button', { class: 'btn ghost ancho', type: 'button' }, `Ver los ${enteros.length - aLaVista.length} más viejos`);
				masViejos.onclick = () => {
					fijarVistaDeEnteros(b, 'todos');
					app.render();
				};
				cuerpo.appendChild(masViejos);
			}
		}
	}
	// La excepción (Agata, 14/9): un precinto sin letra porque faltó la persona de calidad.
	cambiarDeLista('No tiene letra todavía: tarima de espera', true);
	pie(pieNodo, b, { texto: 'Siguiente', deshabilitado: b.precinto === null, alTocar: () => void avanzar(b) });
}

function pBolsas(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorEmbolsar): void {
	const { arriba, medio } = padAnclado(cuerpo, pieNodo);
	const p = b.precinto;
	if (p === null) {
		pregunta(arriba, '¿Cuántas bolsas?', 'Primero elegí el precinto.');
		pie(pieNodo, b);
		return;
	}
	const cambio = precintoAlDia(b);
	b.formato_bolsa_g ??= p.formato_previo ?? FORMATO_PREMARCADO;
	pregunta(arriba, '¿Cuántas bolsas?', `Precinto ${p.precinto} · silo ${p.silo_numero} · ${p.letra === null ? 'sin letra, a una tarima de espera' : `letra ${p.letra}`}. El formato, arriba del teclado.`);
	// El formato como chip: 8,25 premarcado, salvo que el precinto ya se haya embolsado con
	// el otro —un precinto partido reusa su formato (Codex, ronda del plan, 14/9)—.
	const chips = el('div', { class: 'chips' });
	for (const formato of FORMATOS_BOLSA) {
		const fijado = p.formato_previo !== null && p.formato_previo !== formato;
		const chip = el(
			'button',
			{ class: `chip${b.formato_bolsa_g === formato ? ' sel' : ''}`, type: 'button' },
			fijado ? `${formatoTexto(formato)} · ya se embolsó con ${formatoTexto(p.formato_previo ?? 0)}` : `${formatoTexto(formato)} por bolsa`,
		);
		if (fijado) chip.setAttribute('disabled', '');
		chip.onclick = () => {
			if (fijado) return;
			elegirFormato(b, formato);
			guardarYPintar(b);
		};
		chips.appendChild(chip);
	}
	arriba.appendChild(chips);
	const formato = b.formato_bolsa_g;
	// §12.21: con el precinto en una tarima de espera, las primeras bolsas salen de ahí y no cuentan contra el silo
	// (ya están en lo envasado); solo las nuevas descuentan el saldo y pasan por el tope.
	const enTarima = p.en_tarima ?? null;
	const reparto = repartoDeBolsas(b);
	const nuevas = bolsasNuevas(b);
	const gramos = nuevas === null ? 0 : gramosDeCarga(nuevas, formato);
	const saldo = saldoG(p);
	const caja = el('div', { class: 'readout' });
	const grande = el('div', { class: 'big' });
	grande.appendChild(el('span', b.bolsas === null ? { class: 'apagado' } : undefined, b.bolsas === null ? '0' : String(b.bolsas)));
	grande.appendChild(el('span', { class: 'unit' }, 'bolsas'));
	caja.appendChild(grande);
	const lineas: Array<[string, string]> = [
		[`× ${formatoTexto(formato)}`, b.bolsas === null ? '—' : `${kgConDecimales(gramosDeCarga(b.bolsas, formato))} kg`],
		...(enTarima === null
			? []
			: ([
					[`en espera desde ${cuandoCorto(enTarima.abierto_en, ahoraMs())}`, `${enTarima.bolsas} bolsas sin pallet`],
					...(reparto === null
						? []
						: reparto.nuevas > 0
							? [['de la tarima + nuevas del silo', `${reparto.de_la_tarima} + ${reparto.nuevas}`]]
							: [[`de la tarima: ${reparto.de_la_tarima}`, reparto.quedan > 0 ? `quedan ${reparto.quedan} en espera` : 'la tarima termina']]),
				] as Array<[string, string]>)),
		...(enTarima !== null && (nuevas ?? 0) === 0
			? []
			: ([
					p.neto_g === null || saldo === null
						? [`el silo no se pesó${p.envasado_g > 0 ? ` · ya ${kgConDecimales(p.envasado_g)} kg envasados` : ''}`, 'sin saldo']
						: [`el silo tiene ${kg(p.neto_g)} kg${p.envasado_g > 0 ? ` · ya ${kgConDecimales(p.envasado_g)} kg envasados` : ''}`, `quedan ${kgConDecimales(Math.max(0, saldo - gramos))} kg`],
				] as Array<[string, string]>)),
	];
	for (const [etiqueta, texto] of lineas) {
		const fila = el('div', { class: 'barlab' });
		fila.appendChild(el('span', undefined, etiqueta));
		fila.appendChild(el('b', undefined, texto));
		caja.appendChild(fila);
	}
	arriba.appendChild(caja);
	// Por encima del neto más tres bolsas (o el 10 %, si es más) se bloquea (Agata, 7/10: las tres
	// bolsas del limbo entre dos precintos; el 15/9 era el 10 %, «no más que eso»); por debajo,
	// avisos (boceto §3).
	const bloqueado = superaLaTolerancia(p, nuevas, formato);
	if (cambio !== null) medio.appendChild(flag('warn', cambio.texto, ''));
	if (bloqueado) {
		medio.appendChild(
			flag('stop', `Con estas van ${kgConDecimales(p.envasado_g + gramos)} kg de un silo de ${kg(p.neto_g ?? 0)}.`, `Más de ${BOLSAS_DEL_TOPE} bolsas (o del ${POR_CIENTO_DEL_TOPE} %) por encima del silo: no se envasan más kilos que los producidos.`),
		);
	} else if ((nuevas ?? 0) > 0 && sobrepasaElSilo(p, nuevas, formato)) {
		medio.appendChild(flag('warn', `El silo tenía ${kgConDecimales(Math.max(0, saldo ?? 0))} kg y van ${kgConDecimales(gramos)}.`, 'Más de una bolsa de diferencia. ¿Seguro?'));
	} else if ((nuevas ?? 0) > 0 && precintoEntero(p)) {
		medio.appendChild(flag('warn', 'Este precinto ya se embolsó entero.', 'Si quedaban bolsas sin anotar, seguí.'));
	}
	// v6 (§12.4, §12.5): entrando desde la tarjeta del inicio esta es la primera pantalla del precinto,
	// así que sus notas y sus acciones van acá. DESPUÉS de los carteles y en la franja que hace scroll:
	// en la zona fija le comían el lugar al bloqueo del 10 % (visto en el pane el 2/10: con dos notas
	// el cartel quedaba debajo del teclado y «Siguiente» apagado sin explicación).
	if (!b.tarima) {
		const visto = precintosParaEmbolsar().find((x) => x.llenado_id === p.llenado_id);
		if (visto) accionesDelPrecinto(medio, visto, b);
	}
	cuerpo.appendChild(
		teclado(
			(digito) => {
				const actual = String(b.bolsas ?? '');
				if (actual.length >= 2) return;
				b.bolsas = Number(`${actual}${digito}`);
				guardarYPintar(b);
			},
			() => {
				const actual = String(b.bolsas ?? '');
				b.bolsas = actual.length > 1 ? Number(actual.slice(0, -1)) : null;
				guardarYPintar(b);
			},
		),
	);
	pie(pieNodo, b, { texto: 'Siguiente', deshabilitado: !bolsasValidas(b.bolsas) || bloqueado, alTocar: () => void avanzar(b) });
}

/** El pallet abierto elegido, congelado; quiénes embolsan son los del pallet. */
export function elegirDestino(b: BorradorEmbolsar, p: PalletProyectado): void {
	b.destino = palletVisto(p);
	b.nuevo = null;
	b.personas_pallet = [...p.personas];
}

/** «Pallet nuevo»: nace acá, con identidad propia; el rótulo y quiénes lo embolsan se preguntan después. */
export function elegirPalletNuevo(b: BorradorEmbolsar): void {
	b.destino = null;
	b.nuevo ??= { pallet_id: generarUlid(), version_id: generarUlid(), mutacion_id: generarUlid(), rotulo: null };
	if (b.personas_pallet.length === 0) b.personas_pallet = [...b.personas];
}

// ── El rótulo (§5.7 r. 22) ───────────────────────────────────────────────

type ConRotulo = BorradorEmbolsar | BorradorRehacerPallet;

/** El rótulo elegido por el borrador, esté donde esté: en el pallet nuevo del embolsado o de la unión, o en el borrador. */
export function rotuloDe(b: ConRotulo): RotuloElegido | null {
	return b.tipo === 'embolsar' ? (b.nuevo?.rotulo ?? null) : b.rotulo;
}

function ponerRotulo(b: ConRotulo, rotulo: RotuloElegido | null): void {
	if (b.tipo === 'embolsar') {
		if (b.nuevo) b.nuevo.rotulo = rotulo;
	} else b.rotulo = rotulo;
}

/** La letra y el formato del pallet que va a llevar el rótulo: del precinto, del borrador, de la tarima o de la apertura caída. */
function letraYFormatoDe(b: ConRotulo): { letra: string | null; formato: number | null } {
	if (b.tipo === 'embolsar') return { letra: b.precinto?.letra ?? null, formato: b.formato_bolsa_g ?? FORMATO_PREMARCADO };
	const pl = b.apertura.payload as Record<string, unknown>;
	return { letra: typeof pl.letra === 'string' ? pl.letra : null, formato: typeof pl.formato_bolsa_g === 'number' ? pl.formato_bolsa_g : null };
}

/** Los rótulos elegidos por OTROS borradores de este teléfono. */
function elegidosPorOtros(b: ConRotulo): RotuloElegido[] {
	return [...app.rotulosEnBorradores.entries()].filter(([captureId]) => captureId !== b.capture_id).map(([, r]) => r);
}

/**
 * La campaña del rótulo: al rehacer, la del pallet caído —sus cargas son de esa campaña, y un
 * rótulo de la campaña de ahora lo mandaría a `campania_cruzada` (Codex, 15/9)—; si no, la abierta.
 */
function campaniaDelRotulo(b: ConRotulo): string | null {
	if (b.tipo === 'rehacer_pallet') return campaniaDeApertura(b.apertura.payload) ?? campaniaAbiertaId();
	return campaniaAbiertaId();
}

/**
 * Los rótulos que ya están tomados en este teléfono para este borrador: los de los pallets que
 * pasaron por acá (por campaña y número), los elegidos por otros borradores, y —al rehacer— los de
 * las otras aperturas que van a reencolarse en el mismo lote (Codex, 15/9).
 */
function tomadoPara(b: ConRotulo): (campaniaId: string, numero: number) => boolean {
	const tomado = rotuloTomado(app.palletsConocidos.values(), elegidosPorOtros(b));
	const delLote = b.tipo === 'rehacer_pallet' ? new Set(numerosDeAperturas(b.hijos)) : new Set<number>();
	const enBandeja = rotulosCaidos(b);
	return (campaniaId, numero) => tomado(campaniaId, numero) || delLote.has(numero) || enBandeja.some((r) => r.campania_id === campaniaId && r.numero === numero);
}

/**
 * Los rótulos de las aperturas que están en la bandeja siguen tomados —el número de un pallet que cayó
 * colgando de otro no se vuelve a proponer, porque al rehacer el primero el segundo sube con el suyo—,
 * salvo el de la apertura que este mismo borrador rehace: ese se puede tipear igual, si la oficina lo
 * reactivó (Codex, ronda de la v5 entera, 15/9).
 */
function rotulosCaidos(b: ConRotulo): Array<{ campania_id: string; numero: number }> {
	const propia = b.tipo === 'rehacer_pallet' ? b.apertura.id : null;
	return app.rotulosEnBandeja.filter((r) => r.mutacion_id !== propia);
}

/**
 * Los rótulos que se le pueden poner (r. 22): los emitidos sin usar del catálogo para esa letra y
 * formato y esa campaña, menos los que este teléfono ya usó y el catálogo no refleja y los
 * elegidos por otros borradores; el primero emitido primero.
 */
export function rotulosDisponibles(b: ConRotulo): RotuloDelCatalogo[] {
	const { letra, formato } = letraYFormatoDe(b);
	if (letra === null || formato === null) return [];
	// Al rehacer, el rótulo con que la apertura cayó no se vuelve a proponer (un catálogo viejo lo
	// sigue trayendo): se puede tipear igual, si la oficina lo reactivó.
	const caido = b.tipo === 'rehacer_pallet' ? numeroDeApertura(b.apertura.payload) : null;
	const tomado = tomadoPara(b);
	return rotulosPara(app.catalogos?.rotulos_disponibles ?? [], letra, formato, [], [], campaniaDelRotulo(b)).filter((r) => r.numero !== caido && !tomado(r.campania_id, r.numero));
}

/**
 * Por qué un número elegido no puede viajar: ya lo tiene un pallet de este teléfono, lo eligió
 * otro borrador, lo lleva otra apertura del mismo lote, o figura emitido con otra letra o formato
 * (una combinación que cambió después de elegirlo; Codex, 15/9).
 */
export function trabaDelRotulo(b: ConRotulo): string | null {
	const rotulo = rotuloDe(b);
	if (rotulo === null) return null;
	for (const p of app.palletsConocidos.values()) {
		if (p.numero === rotulo.numero && (p.campania_id === null || p.campania_id === rotulo.campania_id)) return 'Ese número ya lo tiene un pallet cargado desde este teléfono.';
	}
	if (elegidosPorOtros(b).some((r) => r.campania_id === rotulo.campania_id && r.numero === rotulo.numero)) return 'Ese número ya lo eligió otro registro a medias en este teléfono.';
	if (b.tipo === 'rehacer_pallet' && numerosDeAperturas(b.hijos).includes(rotulo.numero)) return 'Ese número ya lo lleva otro pallet que vuelve a subir con este.';
	if (rotulosCaidos(b).some((r) => r.campania_id === rotulo.campania_id && r.numero === rotulo.numero)) return 'Ese número lo lleva un pallet que está en la bandeja: rehacelo o quitalo primero.';
	const { letra, formato } = letraYFormatoDe(b);
	const emitido = (app.catalogos?.rotulos_disponibles ?? []).find((r) => r.campania_id === rotulo.campania_id && r.numero === rotulo.numero);
	if (emitido && letra !== null && formato !== null && (emitido.letra !== letra || emitido.formato_bolsa_g !== formato)) {
		return `Ese rótulo es de ${emitido.letra} · ${formatoTexto(emitido.formato_bolsa_g)}, no de ${letra} · ${formatoTexto(formato)}.`;
	}
	return null;
}

/** Elegir un rótulo del catálogo, o uno tipeado. La campaña es la del borrador (`campaniaDelRotulo`); el número se marca si figura entre los disponibles. */
export function elegirRotulo(b: ConRotulo, numero: number | null): void {
	if (numero === null) {
		ponerRotulo(b, null);
		return;
	}
	const campania = campaniaDelRotulo(b);
	if (campania === null) {
		ponerRotulo(b, null);
		return;
	}
	ponerRotulo(b, { campania_id: campania, numero, del_catalogo: rotulosDisponibles(b).some((r) => r.numero === numero) });
}

/** ¿Ya se propuso un rótulo en este borrador? Si la embolsadora lo borró, no se vuelve a proponer (Codex, 15/9). */
function rotuloPropuesto(b: ConRotulo): boolean {
	return b.tipo === 'embolsar' ? (b.nuevo?.rotulo_propuesto ?? false) : (b.rotulo_propuesto ?? false);
}

function marcarPropuesto(b: ConRotulo): void {
	if (b.tipo === 'embolsar') {
		if (b.nuevo) b.nuevo.rotulo_propuesto = true;
	} else b.rotulo_propuesto = true;
}

const MAX_DIGITOS_ROTULO = 6;
const CHIPS_DE_ROTULOS = 8;

/**
 * «¿Qué rótulo le ponés?» (r. 22): viene propuesto el primero emitido de la letra y el formato; la
 * embolsadora confirma que es el papel que tiene en la mano, toca otro de los emitidos o tipea
 * el número. Uno que no figura como emitido viaja igual —el catálogo puede estar viejo— y el
 * servidor decide; uno que ya lleva un pallet de este teléfono no viaja: iría a `rotulo_usado`.
 */
function pRotulo(cuerpo: HTMLElement, pieNodo: HTMLElement, b: ConRotulo): void {
	const { arriba, medio } = padAnclado(cuerpo, pieNodo);
	const { letra, formato } = letraYFormatoDe(b);
	const disponibles = rotulosDisponibles(b);
	// Propuesto el primero emitido una sola vez, sin guardar: el borrador se guarda con el toque siguiente,
	// como el formato. Si la embolsadora lo borró para tipear otro, no se le vuelve a poner.
	if (rotuloDe(b) === null && disponibles.length > 0 && !rotuloPropuesto(b)) {
		elegirRotulo(b, disponibles[0].numero);
		marcarPropuesto(b);
	}
	const elegido = rotuloDe(b);
	const combinacion = `${letra ?? '—'} · ${formato === null ? '—' : formatoTexto(formato)}`;
	pregunta(
		arriba,
		b.tipo === 'rehacer_pallet' ? '¿Qué rótulo le ponés ahora?' : '¿Qué rótulo le ponés?',
		disponibles.length > 0 ? `El papel impreso que va en el pallet. Viene propuesto el primero que se emitió de ${combinacion}.` : `No figura ningún rótulo de ${combinacion} emitido sin usar: tipeá el del papel.`,
	);
	if (disponibles.length > 0) {
		const chips = el('div', { class: 'chips' });
		for (const r of disponibles.slice(0, CHIPS_DE_ROTULOS)) {
			const chip = el('button', { class: `chip${elegido?.numero === r.numero ? ' sel' : ''}`, type: 'button' }, `Nº ${r.numero}`);
			chip.onclick = () => {
				elegirRotulo(b, r.numero);
				guardarYPintar(b);
			};
			chips.appendChild(chip);
		}
		if (disponibles.length > CHIPS_DE_ROTULOS) chips.appendChild(el('span', { class: 'qsub' }, `y ${disponibles.length - CHIPS_DE_ROTULOS} más`));
		arriba.appendChild(chips);
	}
	const caja = el('div', { class: 'readout' });
	const grande = el('div', { class: 'big' });
	grande.appendChild(el('span', elegido === null ? { class: 'apagado' } : undefined, elegido === null ? '0' : String(elegido.numero)));
	grande.appendChild(el('span', { class: 'unit' }, 'rótulo'));
	caja.appendChild(grande);
	const delCatalogo = elegido === null ? undefined : disponibles.find((r) => r.numero === elegido.numero);
	const fila = el('div', { class: 'barlab' });
	fila.appendChild(el('span', undefined, delCatalogo ? `se envasan a partir del ${fechaCorta(delCatalogo.envasado_desde)}` : elegido === null ? 'tocá uno o tipeá el del papel' : 'no figura como emitido'));
	fila.appendChild(el('b', undefined, delCatalogo ? `vence ${fechaCorta(masUnAnio(delCatalogo.envasado_desde))}` : ''));
	caja.appendChild(fila);
	arriba.appendChild(caja);
	const traba = trabaDelRotulo(b);
	if (traba) medio.appendChild(flag('stop', traba, 'Elegí otro papel.'));
	else if (elegido !== null && !delCatalogo) medio.appendChild(flag('warn', `El Nº ${elegido.numero} no figura como emitido sin usar.`, 'Si es el papel que tenés, seguí: la oficina lo revisa cuando suba.'));
	cuerpo.appendChild(
		teclado(
			(digito) => {
				const actual = elegido === null ? '' : String(elegido.numero);
				if (actual.length >= MAX_DIGITOS_ROTULO) return;
				const numero = Number(`${actual}${digito}`);
				elegirRotulo(b, numero === 0 ? null : numero);
				guardarYPintar(b);
			},
			() => {
				const actual = elegido === null ? '' : String(elegido.numero);
				elegirRotulo(b, actual.length > 1 ? Number(actual.slice(0, -1)) : null);
				guardarYPintar(b);
			},
		),
	);
	pie(pieNodo, b, { texto: 'Siguiente', deshabilitado: elegido === null || traba !== null, alTocar: () => void avanzar(b) });
}


function pPallet(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorEmbolsar): void {
	const p = b.precinto;
	const formato = b.formato_bolsa_g ?? FORMATO_PREMARCADO;
	if (p === null) {
		pregunta(cuerpo, '¿A qué pallet?', 'Primero elegí el precinto.');
		pie(pieNodo, b);
		return;
	}
	const letra = p.letra ?? '—';
	const abierto = palletAbiertoPara(p.letra, formato);
	// Un pallet en armado por letra y formato (Agata, 14/9): el que hay viene marcado y
	// «pallet nuevo» no se ofrece; sin ninguno, se abre uno. Lo elegido antes se revisa
	// contra lo de ahora: si ese pallet ya no está, o apareció uno mientras se cargaba
	// (otro teléfono lo abrió), el servidor rechazaría con `pallet_abierto_repetido`.
	if (b.destino !== null && abierto?.pallet_id !== b.destino.pallet_id) b.destino = null;
	if (b.nuevo !== null && abierto) b.nuevo = null;
	if (b.destino === null && b.nuevo === null && abierto) elegirDestino(b, abierto);
	pregunta(
		cuerpo,
		'¿A qué pallet?',
		abierto ? `El de ${letra} · ${formatoTexto(formato)} en armado viene marcado.` : `No hay pallet de ${letra} · ${formatoTexto(formato)} en armado: se abre uno.`,
	);
	const grilla = el('div', { class: 'grid1' });
	if (abierto) {
		const tarjeta = tarjetaPallet(abierto, {
			alTocar: () => {
				elegirDestino(b, abierto);
				guardarYPintar(b);
			},
		});
		tarjeta.setAttribute('aria-pressed', b.destino?.pallet_id === abierto.pallet_id ? 'true' : 'false');
		grilla.appendChild(tarjeta);
	}
	// Los de la misma letra con otro formato se ven apagados, con el porqué: no se mezcla.
	for (const otro of palletsAbiertos().filter((x) => x.es_tarima === 0 && x.letra === p.letra && x.formato_bolsa_g !== formato)) {
		grilla.appendChild(tarjetaPallet(otro, { apagado: `es de ${formatoTexto(otro.formato_bolsa_g)}: otro formato, no se mezcla` }));
	}
	const nuevo = el('button', { class: `opt${b.nuevo !== null ? ' sel' : ''}`, type: 'button', 'aria-pressed': b.nuevo !== null ? 'true' : 'false' });
	const textoNuevo = el('span', undefined, 'Pallet nuevo');
	textoNuevo.appendChild(
		el('small', undefined, abierto ? `ya hay uno de ${letra} · ${formatoTexto(formato)} en armado: se completa ese` : `${NOMBRE_LETRA_PALLET[letra as LetraPallet] ?? letra} · ${formatoTexto(formato)}`),
	);
	nuevo.appendChild(textoNuevo);
	if (abierto) nuevo.setAttribute('disabled', '');
	else
		nuevo.onclick = () => {
			elegirPalletNuevo(b);
			guardarYPintar(b);
		};
	grilla.appendChild(nuevo);
	cuerpo.appendChild(grilla);
	if (b.destino !== null && b.bolsas !== null) {
		const total = b.destino.bolsas + b.bolsas;
		if (total > BOLSAS_ESTANDAR) cuerpo.appendChild(flag('warn', `Con estas, el pallet queda en ${total} bolsas.`, `Se pasa de ${BOLSAS_ESTANDAR}: cargá las que entran y abrí otro.`));
		else if (total === BOLSAS_ESTANDAR) cuerpo.appendChild(flag('ok', `Con estas llega a ${BOLSAS_ESTANDAR} bolsas.`, 'Después de cargar, la app propone cerrarlo.'));
	}
	pie(pieNodo, b, { texto: 'Siguiente', deshabilitado: b.destino === null && b.nuevo === null, alTocar: () => void avanzar(b) });
}

function pPersonas(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorEmbolsar): void {
	if (b.personas_pallet.length === 0) b.personas_pallet = [...personaPremarcada()];
	pregunta(cuerpo, '¿Quiénes embolsan?', `Hasta ${MAX_PERSONAS_PALLET}. Se anotan en el pallet: quedan en cada precinto que se le cargue.`);
	// Quién embolsó, no quién carga: acá el dueño del teléfono también se desmarca (boceto; Codex, 15/9).
	cuerpo.appendChild(grillaPersonas(b.personas_pallet, MAX_PERSONAS_PALLET, () => guardarYPintar(b), 'envasado', { soltarDuenio: true }));
	if (b.personas_pallet.length >= MAX_PERSONAS_PALLET) cuerpo.appendChild(el('div', { class: 'qsub despues' }, `${MAX_PERSONAS_PALLET} es el máximo.`));
	pie(pieNodo, b, { texto: 'Siguiente', deshabilitado: b.personas_pallet.length === 0, alTocar: () => void avanzar(b) });
}

/** Elegir el pallet a cerrar congela lo que se vio de él: la base del CAS y el testigo de composición (r. 17b). */
export function elegirPallet(b: BorradorCerrarPallet | BorradorAnularPallet, p: PalletProyectado): void {
	b.pallet = palletVisto(p);
}

/** Cerrar y tirar eligen entre lo que hay en armado. */
function pCual(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorCerrarPallet | BorradorAnularPallet): void {
	const grilla = el('div', { class: 'grid1' });
	const tira = b.tipo === 'anular_pallet';
	pregunta(cuerpo, tira ? '¿Qué pallet se tira?' : '¿Qué pallet cerrás?', 'Con lo que lleva.');
	const elegir = (p: PalletProyectado) => {
		elegirPallet(b, p);
		guardarYPintar(b);
	};
	for (const p of palletsAbiertos().filter((x) => x.es_tarima === 0)) {
		const tarjeta = tarjetaPallet(p, { alTocar: () => elegir(p) });
		tarjeta.setAttribute('aria-pressed', b.pallet?.pallet_id === p.pallet_id ? 'true' : 'false');
		grilla.appendChild(tarjeta);
	}
	// Una tarima en espera también se puede tirar (metales antes de que llegue la letra); cerrarse, no: se une. Una con
	// bolsas ya repartidas en un pallet, no (§12.21): primero la oficina deshace el pase.
	if (tira) for (const t of palletsAbiertos().filter((x) => x.es_tarima === 1 && x.tiene_partes !== 1)) grilla.appendChild(tarjetaTarima(t, { alTocar: () => elegir(t), seleccionado: b.pallet?.pallet_id === t.pallet_id }));
	cuerpo.appendChild(grilla);
	pie(pieNodo, b, { texto: 'Siguiente', deshabilitado: b.pallet === null, alTocar: () => void avanzar(b) });
}

/**
 * «¿Cuántas bolsas fueron?» (§12.18; Agata, 15/10: «tienen que poder editar el número de bolsas»): lo que tiene ahora
 * arriba, el mismo número apaga «Siguiente», el tope del precinto sin la carga que se corrige (bajar nunca se bloquea) y,
 * con el pallet cerrado, en cuántas queda: sigue cerrado, y si el rótulo dice otra cosa la oficina prepara otro.
 */
function pBolsasCarga(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorCorregirCarga): void {
	const { arriba, medio } = padAnclado(cuerpo, pieNodo);
	const c = b.carga;
	if (c === null) {
		pregunta(arriba, '¿Cuántas bolsas fueron?', 'No hay ninguna carga elegida.');
		pie(pieNodo, b);
		return;
	}
	const cuando = c.registrado_en === '' ? '' : ` ${cuandoCorto(c.registrado_en, ahoraMs(), true)}`;
	pregunta(arriba, '¿Cuántas bolsas fueron?', `Precinto ${c.precinto ?? '—'} · ${palletDeLaCargaTexto(c)}. La cargó ${nombreDe(c.persona_id)}${cuando}.`);
	const caja = el('div', { class: 'readout' });
	const grande = el('div', { class: 'big' });
	grande.appendChild(el('span', b.bolsas === null ? { class: 'apagado' } : undefined, b.bolsas === null ? '0' : String(b.bolsas)));
	grande.appendChild(el('span', { class: 'unit' }, 'bolsas'));
	caja.appendChild(grande);
	const queda = c.pallet_bolsas - c.cant_bolsas + (b.bolsas ?? c.cant_bolsas);
	for (const [etiqueta, texto] of [
		['Ahora', c.cant_bolsas === 1 ? '1 bolsa' : `${c.cant_bolsas} bolsas`],
		['el pallet queda en', `${queda} bolsas`],
	] as Array<[string, string]>) {
		const fila = el('div', { class: 'barlab' });
		fila.appendChild(el('span', undefined, etiqueta));
		fila.appendChild(el('b', undefined, texto));
		caja.appendChild(fila);
	}
	arriba.appendChild(caja);
	const igual = b.bolsas !== null && b.bolsas === c.cant_bolsas;
	const precinto = precintoDeLaCarga(c);
	const tope = b.bolsas !== null && precinto !== null && superaLaToleranciaAlCorregir(precinto, c.cant_bolsas, b.bolsas, c.formato_bolsa_g);
	if (igual) medio.appendChild(flag('warn', 'Es lo que ya tiene.', 'No hay nada que corregir.'));
	else if (tope && precinto !== null)
		medio.appendChild(
			flag('stop', `Con ${b.bolsas} bolsas el precinto pasa el tope: un silo de ${kg(precinto.neto_g ?? 0)} kg.`, `Más de ${BOLSAS_DEL_TOPE} bolsas (o del ${POR_CIENTO_DEL_TOPE} %) por encima del silo.`),
		);
	else if (c.pallet_estado === 'cerrado' && b.bolsas !== null) medio.appendChild(flag('warn', `El pallet está cerrado: queda en ${queda} bolsas.`, 'Sigue cerrado. Si el rótulo dice otra cantidad, la oficina prepara otro.'));
	if (esLaUnicaDeUnCerrado(c)) medio.appendChild(el('div', { class: 'qsub despues' }, 'Es la única carga del pallet: para sacarla entera, la oficina anula el pallet.'));
	else {
		const anular = el('button', { class: 'enlace', type: 'button' }, 'Anular esta carga');
		anular.onclick = () => void descartarBorrador(b).then(() => acciones.anularCarga(c));
		medio.appendChild(anular);
	}
	cuerpo.appendChild(
		teclado(
			(digito) => {
				const actual = String(b.bolsas ?? '');
				if (actual.length >= 2) return;
				b.bolsas = Number(`${actual}${digito}`);
				guardarYPintar(b);
			},
			() => {
				const actual = String(b.bolsas ?? '');
				b.bolsas = actual.length > 1 ? Number(actual.slice(0, -1)) : null;
				guardarYPintar(b);
			},
		),
	);
	pie(pieNodo, b, { texto: 'Siguiente', deshabilitado: !bolsasValidas(b.bolsas) || igual || tope, alTocar: () => void avanzar(b) });
}

/** «¿Por qué se anula?» (§12.18): se anotó dos veces, era de otro precinto, u otro con el motivo en una línea. Queda en el historial. */
function pMotivoCarga(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorAnularCarga): void {
	const c = b.carga;
	pregunta(cuerpo, '¿Por qué se anula?', c === null ? '' : `Precinto ${c.precinto ?? '—'} · ${c.cant_bolsas === 1 ? '1 bolsa' : `${c.cant_bolsas} bolsas`} · la cargó ${nombreDe(c.persona_id)}. Queda en el historial.`);
	const grilla = el('div', { class: 'grid1' });
	for (const [motivo, detalle] of MOTIVOS_ANULACION_CARGA) {
		const opcion = el('button', { class: `opt${b.motivo === motivo ? ' sel' : ''}`, type: 'button', 'aria-pressed': b.motivo === motivo ? 'true' : 'false' });
		const texto = el('span', undefined, motivo);
		texto.appendChild(el('small', undefined, detalle));
		opcion.appendChild(texto);
		opcion.onclick = () => {
			b.motivo = motivo;
			guardarYPintar(b);
		};
		grilla.appendChild(opcion);
	}
	cuerpo.appendChild(grilla);
	if (b.motivo === 'otro') {
		cajaDeTexto(
			cuerpo,
			() => b.motivo_otro,
			(v) => {
				b.motivo_otro = v;
			},
			'Ej.: se cargó en el pallet de la otra letra',
			() => void guardarBorrador(b),
		);
	}
	pie(pieNodo, b, { texto: 'Siguiente', deshabilitado: b.motivo === null, alTocar: () => void avanzar(b) });
}

/** «¿Por qué se tira?» (boceto): metales es la revisión de los imanes; otro, lo demás. Las bolsas no vuelven al silo. */
function pMotivo(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorAnularPallet): void {
	pregunta(cuerpo, '¿Por qué se tira?', 'Las bolsas no vuelven al silo: la merma se conoce.');
	const grilla = el('div', { class: 'grid1' });
	for (const [motivo, detalle] of MOTIVOS_ANULACION_PALLET) {
		const opcion = el('button', { class: `opt${b.motivo === motivo ? ' sel' : ''}`, type: 'button', 'aria-pressed': b.motivo === motivo ? 'true' : 'false' });
		const texto = el('span', undefined, motivo);
		texto.appendChild(el('small', undefined, detalle));
		opcion.appendChild(texto);
		opcion.onclick = () => {
			b.motivo = motivo;
			guardarYPintar(b);
		};
		grilla.appendChild(opcion);
	}
	cuerpo.appendChild(grilla);
	pie(pieNodo, b, { texto: 'Siguiente', deshabilitado: b.motivo === null, alTocar: () => void avanzar(b) });
}

function pHora(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorEmbolsado): void {
	const titulo = b.tipo === 'cerrar_pallet' ? '¿A qué hora se cerró?' : b.tipo === 'anular_pallet' ? '¿A qué hora se tiró?' : '¿A qué hora se cargó?';
	pregunta(cuerpo, titulo, 'Si fue recién, es ahora.');
	const leer = (): number => {
		if (b.tipo === 'embolsar') return (b.registrado_ms ??= ahoraMs());
		if (b.tipo === 'cerrar_pallet' || b.tipo === 'anular_pallet') return (b.cerrado_ms ??= ahoraMs());
		return ahoraMs(); // rehacer, cambiar la calidad y la nota no pasan por acá
	};
	reloj(cuerpo, leer(), b.tipo === 'cerrar_pallet' ? 'hora del cierre' : 'hora', (nuevo) => {
		if (b.tipo === 'embolsar') b.registrado_ms = nuevo;
		else if (b.tipo === 'cerrar_pallet' || b.tipo === 'anular_pallet') b.cerrado_ms = nuevo;
		guardarYPintar(b);
	});
	let ok = true;
	// Una carga no puede quedar antes de la apertura del pallet: un borrador que nació a las 08:00 y
	// se retomó a las 10:00 con un pallet abierto a las 09:00 iba derecho a `carga_antes_del_pallet`.
	if (b.tipo === 'embolsar' && !cargaDentroDelPallet(b.registrado_ms, b.destino)) {
		ok = false;
		cuerpo.appendChild(flag('stop', 'Tiene que ser después de la apertura del pallet.', `El pallet se abrió ${(b.destino?.abierto_en ?? '').slice(11)}.`));
	}
	// Cerrar, tirar o pasar la tarima (§12.21: la hora de la carga es la del pase) tampoco pueden quedar antes de la
	// apertura ni de la última carga (r. 17).
	const cierre =
		b.tipo === 'cerrar_pallet' || b.tipo === 'anular_pallet'
			? { hora: b.cerrado_ms, de: b.pallet }
			: b.tipo === 'embolsar' && b.precinto?.en_tarima
				? { hora: b.registrado_ms, de: b.precinto.en_tarima }
				: null;
	if (cierre && !cierreDePalletValido(cierre.hora, cierre.de)) {
		ok = false;
		cuerpo.appendChild(
			flag(
				'stop',
				'Tiene que ser después de la apertura y de la última carga.',
				`Abierto ${(cierre.de?.abierto_en ?? '').slice(11)}${cierre.de?.ultima_carga_en ? ` · última carga ${cierre.de.ultima_carga_en.slice(11)}` : ''}.`,
			),
		);
	}
	pie(pieNodo, b, { texto: 'Siguiente', deshabilitado: !ok, alTocar: () => void avanzar(b) });
}

// ── Revisión y guardado ───────────────────────────────────────────────────

type Fila = [string, string, number | null];

export function filasDe(b: BorradorEmbolsado): Fila[] {
	const pasos = pasosDe(b);
	const indice = (nombre: string) => {
		const i = pasos.indexOf(nombre);
		return i === -1 ? null : i;
	};
	const quien = b.personas[0] ? nombreDe(b.personas[0]) : '—';
	// El «quién» de un teléfono personal no se cambia desde la revisión (10/9).
	const pasoQuien = conPersonaHabitual(b.personas) ? null : indice('quien');
	const rotuloTexto = (r: RotuloElegido | null) => (r === null ? '—' : `Nº ${r.numero}${r.del_catalogo ? '' : ' · no figura como emitido'}`);
	switch (b.tipo) {
		case 'embolsar': {
			const p = b.precinto;
			const formato = b.formato_bolsa_g ?? FORMATO_PREMARCADO;
			const bolsas = b.bolsas ?? 0;
			const destino = b.tarima
				? 'tarima de espera (nueva)'
				: b.nuevo !== null
					? `pallet nuevo · ${p?.letra ?? '—'} · ${formatoTexto(formato)}`
					: b.destino
						? `pallet ${nombreDePallet(b.destino)} · ${b.destino.letra ?? '—'} · ${formatoTexto(formato)}`
						: '—';
			// §12.21: con el precinto en una tarima de espera son dos registros a la vista: lo que pasa de la tarima y, si
			// hay, las bolsas nuevas del silo.
			const enTarima = b.tarima ? null : (p?.en_tarima ?? null);
			const reparto = enTarima === null ? null : repartoDeBolsas(b);
			return [
				['Quién', quien, pasoQuien],
				['Precinto', p ? `${p.precinto} · silo ${p.silo_numero} · ${p.lote} · ${p.letra === null ? 'sin letra' : `letra ${p.letra}`}` : '—', indice('precinto')],
				['Bolsas', `${bolsas} × ${formatoTexto(formato)} = ${kgConDecimales(gramosDeCarga(bolsas, formato))} kg`, indice('bolsas')],
				...(enTarima === null
					? []
					: ([
							[
								'De la tarima',
								reparto === null
									? `${enTarima.bolsas} bolsas en espera desde ${cuandoCorto(enTarima.abierto_en, ahoraMs())}`
									: `${reparto.de_la_tarima} de ${enTarima.bolsas} bolsas → ${destino}${reparto.quedan > 0 ? ` · quedan ${reparto.quedan} en espera` : ' · la tarima termina'}`,
								null,
							],
							...(reparto !== null && reparto.nuevas > 0 ? [['Nuevas del silo', `${reparto.nuevas} ${reparto.nuevas === 1 ? 'bolsa' : 'bolsas'} → ${destino}`, null]] : []),
						] as Fila[])),
				['A dónde', destino, b.tarima ? null : indice('pallet')],
				...(b.nuevo !== null && !b.tarima ? [['Rótulo', rotuloTexto(b.nuevo.rotulo), indice('rotulo')] satisfies Fila] : []),
				['Quiénes embolsan', personasTexto(b.tarima && b.personas_pallet.length === 0 ? b.personas : b.personas_pallet), b.nuevo !== null && !b.tarima ? indice('personas') : null],
				['A las', b.registrado_ms === null ? '—' : cuandoCorto(instanteLocal(b.registrado_ms), ahoraMs()), indice('hora')],
			];
		}
		case 'anular_pallet': {
			const p = b.pallet;
			const esTarima = p !== null && p.letra === null;
			return [
				[esTarima ? 'Tarima' : 'Pallet', p ? (esTarima ? nombreDeTarima(p) : `${nombreDePallet(p)} · ${NOMBRE_LETRA_PALLET[p.letra as LetraPallet] ?? p.letra ?? '—'} · ${formatoTexto(p.formato_bolsa_g)}`) : '—', b.desde_inicio ? null : indice('cual')],
				['Quién', quien, pasoQuien],
				['Bolsas', p ? `${p.bolsas} · ${kgConDecimales(gramosDeCarga(p.bolsas, p.formato_bolsa_g))} kg · ${p.precintos === 1 ? '1 precinto' : `${p.precintos} precintos`}` : '—', null],
				['Motivo', b.motivo ?? '—', indice('motivo')],
				['Se tira a las', b.cerrado_ms === null ? '—' : cuandoCorto(instanteLocal(b.cerrado_ms), ahoraMs()), indice('hora')],
			];
		}
		case 'rehacer_pallet': {
			const { letra, formato } = letraYFormatoDe(b);
			const pl = b.apertura.payload as Record<string, unknown>;
			const cargas = b.hijos.filter((h) => h.tipo === 'envasado.crear');
			const bolsas = cargas.reduce((suma, h) => suma + (typeof (h.payload as Record<string, unknown>).cant_bolsas === 'number' ? ((h.payload as Record<string, unknown>).cant_bolsas as number) : 0), 0);
			return [
				['Pallet', `${letra ?? '—'} · ${formato === null ? '—' : formatoTexto(formato)} · rótulo anterior Nº ${typeof pl.numero === 'number' ? pl.numero : '—'}`, null],
				['Por qué no entró', QUE_PASO_ROTULO[b.motivo] ?? b.motivo, null],
				['Lo que vuelve a subir', `la apertura${cargas.length > 0 ? `, ${cargas.length === 1 ? '1 carga' : `${cargas.length} cargas`} (${bolsas} bolsas)` : ''}${b.hijos.some((h) => h.tipo === 'pallet.cerrar') ? ' y el cierre' : ''}`, null],
				['Rótulo nuevo', rotuloTexto(b.rotulo), indice('rotulo')],
			];
		}
		case 'cambiar_letra':
			return [
				['Precinto', `${b.precinto ?? '—'} · silo ${b.silo_numero ?? '—'}`, null],
				['Quién', quien, pasoQuien],
				['Calidad', `${b.letra_actual === null ? '—' : NOMBRE_LETRA_MUESTRA[b.letra_actual]} → ${b.letra === null ? '—' : NOMBRE_LETRA_MUESTRA[b.letra]}`, indice('nueva_letra')],
				['Por qué', textoValido(b.motivo) ?? '— (obligatorio)', indice('motivo_letra')],
			];
		case 'nota_precinto':
			return [
				['Precinto', b.precinto ?? '—', null],
				['Quién', quien, pasoQuien],
				['Nota', textoValido(b.texto) ?? '— (escribí algo)', indice('texto_nota')],
			];
		case 'corregir_carga': {
			const c = b.carga;
			return [
				['Precinto', c ? `${c.precinto ?? '—'} · ${palletDeLaCargaTexto(c)}` : '—', null],
				['Bolsas', c ? `${c.cant_bolsas} → ${b.bolsas ?? '—'} × ${formatoTexto(c.formato_bolsa_g)}` : '—', indice('bolsas_carga')],
				['Quién corrige', quien, pasoQuien],
				['El pallet queda en', c && b.bolsas !== null ? `${c.pallet_bolsas - c.cant_bolsas + b.bolsas} bolsas` : '—', null],
			];
		}
		case 'anular_carga': {
			const c = b.carga;
			return [
				['Precinto', c ? `${c.precinto ?? '—'} · ${c.cant_bolsas === 1 ? '1 bolsa' : `${c.cant_bolsas} bolsas`} · ${palletDeLaCargaTexto(c)}` : '—', null],
				['Motivo', motivoDeAnularCarga(b) ?? '— (obligatorio)', indice('motivo_carga')],
				['Quién anula', quien, pasoQuien],
				['El pallet queda en', c ? `${c.pallet_bolsas - c.cant_bolsas} bolsas` : '—', null],
			];
		}
		default: {
			const p = b.pallet;
			return [
				['Pallet', p ? `${nombreDePallet(p)} · ${NOMBRE_LETRA_PALLET[p.letra as LetraPallet] ?? p.letra ?? '—'} · ${formatoTexto(p.formato_bolsa_g)}` : '—', b.desde_inicio ? null : indice('cual')],
				['Quién', quien, pasoQuien],
				['Bolsas', p ? `${p.bolsas} · ${kgConDecimales(gramosDeCarga(p.bolsas, p.formato_bolsa_g))} kg · ${p.precintos === 1 ? '1 precinto' : `${p.precintos} precintos`}` : '—', null],
				['Quiénes embolsaron', p ? personasTexto(p.personas) : '—', null],
				['Cerrado a las', b.cerrado_ms === null ? '—' : cuandoCorto(instanteLocal(b.cerrado_ms), ahoraMs()), indice('hora')],
			];
		}
	}
}

/** Lo que va a quedar impreso (boceto: cerrar): partida, bolsas, kilos, precintos, vencimiento. */
function rotulo(p: PalletVisto): HTMLElement {
	const caja = el('div', { class: 'ticket rotulo' });
	caja.appendChild(el('div', { class: 'partida' }, `Partida ${partida(p.campania_letra ?? letraDeCampania() ?? '', p.letra ?? '—', p.formato_bolsa_g)}`));
	const primera = p.primera_carga_en === null ? ahoraMs() : msDeInstanteLocal(p.primera_carga_en);
	const filas: Array<[string, string]> = [
		['Pallet', nombreDePallet(p)],
		['Bolsas', `${p.bolsas} × ${formatoTexto(p.formato_bolsa_g)}`],
		['Kilos', `${kgConDecimales(gramosDeCarga(p.bolsas, p.formato_bolsa_g))} kg`],
		['Precintos', String(p.precintos)],
		['Vence', venceEn(primera)],
	];
	for (const [etiqueta, valor] of filas) {
		const fila = el('div', { class: 'row' });
		fila.appendChild(el('span', undefined, etiqueta));
		fila.appendChild(el('b', undefined, valor));
		caja.appendChild(fila);
	}
	return caja;
}

function pRevisar(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorEmbolsado): void {
	// El saldo y el formato del precinto son los de ahora: lo que bloquea o destraba se ve acá, no
	// recién al mantener apretado.
	const cambioDePrecinto = b.tipo === 'embolsar' ? precintoAlDia(b)?.texto ?? null : null;
	pregunta(cuerpo, '¿Está todo bien?', 'Tocá una fila para cambiarla.');
	const resumen = el('div', { class: 'sum' });
	for (const [etiqueta, valor, paso] of filasDe(b)) {
		const fila = el('div', { class: 'r' });
		const textos = el('div');
		textos.appendChild(el('div', { class: 'lb' }, etiqueta));
		textos.appendChild(el('div', { class: 'vl' }, valor));
		fila.appendChild(textos);
		if (paso !== null) {
			const cambiar = el('button', { class: 'fix', type: 'button' }, 'cambiar');
			cambiar.onclick = () => void irAlPaso(b, paso);
			fila.appendChild(cambiar);
		}
		resumen.appendChild(fila);
	}
	cuerpo.appendChild(resumen);

	let bloqueado = false;
	if (b.tipo === 'embolsar' && (cambioDePrecinto !== null || (b.destino === null && b.nuevo === null))) {
		bloqueado = true;
		cuerpo.appendChild(flag('stop', cambioDePrecinto ?? 'Falta el pallet.', 'Tocá «A dónde» para elegirlo.'));
	}
	// §12.21: el tope y el aviso del silo cuentan solo las bolsas nuevas; las de la tarima ya están envasadas.
	if (b.tipo === 'embolsar' && b.precinto !== null && superaLaTolerancia(b.precinto, bolsasNuevas(b), b.formato_bolsa_g)) {
		bloqueado = true;
		cuerpo.appendChild(flag('stop', `Más de ${BOLSAS_DEL_TOPE} bolsas (o del ${POR_CIENTO_DEL_TOPE} %) por encima del silo.`, 'No se envasan más kilos que los producidos. Tocá «Bolsas» para corregirlas.'));
	} else if (b.tipo === 'embolsar' && b.precinto !== null && (bolsasNuevas(b) ?? 0) > 0 && sobrepasaElSilo(b.precinto, bolsasNuevas(b), b.formato_bolsa_g)) {
		cuerpo.appendChild(flag('warn', 'Más kilos que el silo.', 'Se guarda igual: el remanente de la línea se conoce.'));
	}
	if (b.tipo === 'embolsar' && !cargaDentroDelPallet(b.registrado_ms, b.destino)) {
		bloqueado = true;
		cuerpo.appendChild(flag('stop', 'La hora de la carga es anterior a la apertura del pallet.', 'Tocá «A las» para corregirla.'));
	}
	if (b.tipo === 'embolsar' && b.precinto?.en_tarima && !cierreDePalletValido(b.registrado_ms, b.precinto.en_tarima)) {
		bloqueado = true;
		cuerpo.appendChild(flag('stop', 'La hora no puede ser anterior a la apertura de la tarima ni a su última carga.', 'Tocá «A las» para corregirla.'));
	}
	if (b.tipo === 'anular_pallet') {
		// Boceto: «Tirar un pallet no tiene vuelta desde el teléfono. Si fue un error, lo reabre la oficina.»
		cuerpo.appendChild(flag('stop', 'Tirar un pallet no tiene vuelta desde el teléfono.', 'Si fue un error, lo reabre la oficina. Las bolsas no vuelven al silo.'));
		if (!cierreDePalletValido(b.cerrado_ms, b.pallet)) {
			bloqueado = true;
			cuerpo.appendChild(flag('stop', 'La hora no puede ser anterior a la apertura ni a la última carga.', 'Tocá «Se tira a las» para corregirla.'));
		}
	}
	if (b.tipo === 'cerrar_pallet') {
		const p = b.pallet;
		if (p === null || p.bolsas < 1) {
			bloqueado = true;
			cuerpo.appendChild(flag('stop', 'Un pallet vacío no se cierra.', 'Cargale un precinto primero.'));
		} else {
			cuerpo.appendChild(rotulo(p));
			if (p.bolsas < BOLSAS_ESTANDAR) cuerpo.appendChild(flag('warn', `Tiene ${p.bolsas} bolsas, no ${BOLSAS_ESTANDAR}.`, 'Un pallet incompleto se completa con otro precinto; cerrarlo así es la excepción.'));
			else if (p.bolsas > BOLSAS_ESTANDAR) cuerpo.appendChild(flag('warn', `Tiene ${p.bolsas} bolsas: más de ${BOLSAS_ESTANDAR}.`, ''));
			cuerpo.appendChild(
				el(
					'div',
					{ class: 'qsub despues' },
					`Vence al año del primer envasado. El rótulo ya está en el pallet; si lo impreso no coincide con esto, la oficina prepara otro.${app.enLinea ? '' : ' Sin señal: sube cuando haya.'}`,
				),
			);
			if (!cierreDePalletValido(b.cerrado_ms, p)) {
				bloqueado = true;
				cuerpo.appendChild(flag('stop', 'La hora de cierre no puede ser anterior a la apertura ni a la última carga.', 'Tocá «Cerrado a las» para corregirla.'));
			}
		}
	}
	pie(pieNodo, b);
	if (!bloqueado) {
		const texto =
			b.tipo === 'cerrar_pallet'
				? 'Mantené para cerrar'
				: b.tipo === 'rehacer_pallet'
					? 'Mantené para mandarlo'
					: b.tipo === 'anular_pallet'
						? 'Mantené para tirarlo'
						: b.tipo === 'corregir_carga'
							? 'Mantené para corregir'
							: b.tipo === 'anular_carga'
								? 'Mantené para anular'
								: 'Mantené apretado';
		pieNodo.appendChild(botonMantener(texto, 1500, () => void confirmar(b)));
	}
}

function padreDe(padre: string | null | undefined): { padre_id?: string } {
	return padre === null || padre === undefined ? {} : { padre_id: padre };
}

/**
 * La letra y el formato de un pallet sin apertura pendiente: del catálogo, o de lo que pasó por la
 * cola (una apertura ya con acuse que el catálogo todavía no trae). Sin lo segundo, la apertura
 * nueva no esperaba al cierre de ese pallet y viajaba a `pallet_abierto_repetido` (Codex, 14/9).
 */
function letraFormatoConocido(palletId: string): { letra: string | null; formato_bolsa_g: number } | null {
	const p = (app.catalogos?.pallets_abiertos ?? []).find((x) => x.pallet_id === palletId) ?? app.palletsConocidos.get(palletId);
	return p ? { letra: p.letra, formato_bolsa_g: p.formato_bolsa_g } : null;
}

/** A qué pallet pertenece una carga que vino del catálogo, o que la proyección conoce (las dos cargas de un pase pendiente, §12.21). */
function palletDeEnvasadoDelCatalogo(envasadoId: string): string | null {
	for (const p of [...(app.catalogos?.pallets_abiertos ?? []), ...(app.catalogos?.pallets_cerrados_recientes?.pallets ?? []), ...palletsAbiertos(), ...app.palletsCerrados]) {
		if (p.envasados.some((e) => e.envasado_id === envasadoId || e.raiz_id === envasadoId)) return p.pallet_id;
		for (const t of p.tarimas) if (t.envasados.some((e) => e.envasado_id === envasadoId || e.raiz_id === envasadoId)) return t.pallet_id;
	}
	return null;
}

/**
 * Lo que una confirmación deja en la cola, en el orden en que el servidor tiene que
 * aplicarlas, con sus dependencias (§5.7 r. 20): la carga espera la apertura local de su
 * pallet; «pallet nuevo» dentro de embolsar es la apertura y detrás la carga, que la espera;
 * abrir espera el cierre o la anulación pendiente de la misma letra y formato; cerrar espera
 * la última pendiente que toque el pallet.
 */
export function paraLaCola(b: BorradorEmbolsado, pendientes: FilaPendiente[], enCola: Set<string> = new Set(pendientes.map((f) => f.id))): MutacionAEncolar[] {
	const apertura = (
		ids: { pallet_id: string; version_id: string },
		mutacionId: string,
		letra: string | null,
		formato: number | null,
		personasPallet: string[],
		abiertoMs: number | null,
		nota: string | null,
		rotulo: RotuloElegido | null,
		tarima: { campania_id: string | null; llenado_id: string | null } | null = null,
		padreExtra?: string,
	): MutacionAEncolar => ({
		id: mutacionId,
		personas: [...b.personas],
		tipo: 'pallet.abrir',
		// La campaña y el número viajan con el rótulo elegido (r. 22); la tarima no tiene rótulo y lleva la del precinto.
		payload: armarPayloadPalletAbrir({ ...ids, letra, formato_bolsa_g: formato, personas_pallet: personasPallet, abierto_ms: abiertoMs, nota, persona: b.personas[0], rotulo, tarima }),
		entidad: 'pallet',
		registro_id: ids.pallet_id,
		// Una tarima no tiene tope por letra y formato (r. 17a), pero su carga sí espera a que la
		// anulación o la unión pendiente de otra tarima del mismo precinto suba (`tarima_repetida`);
		// un pallet espera al cierre pendiente de su letra y formato, y a lo que el llamador sume.
		...padreDe(
			tarima !== null
				? tarima.llenado_id === null
					? null
					: padreDeTarima(pendientes, tarima.llenado_id, llenadoDeTarimaDelCatalogo)
				: masNuevaDe(pendientes, [letra === null || formato === null ? undefined : padreDeApertura(pendientes, letra, formato, letraFormatoConocido), padreExtra]),
		),
		resumen: {
			entidad: 'pallet',
			fecha: fechaDeProduccion(abiertoMs ?? ahoraMs()),
			accion: 'abrio',
			es_tarima: tarima !== null,
			letra: tarima !== null ? null : ((letra ?? null) as LetraPallet | null),
			formato_bolsa_g: formato ?? 0,
			bolsas: null,
			numero: rotulo?.numero ?? null,
		},
	});
	switch (b.tipo) {
		case 'embolsar': {
			const p = b.precinto;
			const formato = b.formato_bolsa_g ?? FORMATO_PREMARCADO;
			const fecha = fechaDeProduccion(b.registrado_ms ?? ahoraMs());
			// «Lo cargado» dice a qué pallet fue cada carga, con su número (§12.18): el del rótulo elegido o el del destino.
			const numero = b.tarima ? null : b.nuevo !== null ? (b.nuevo.rotulo?.numero ?? null) : (b.destino?.numero ?? null);
			const resumen = {
				entidad: 'envasado' as const,
				fecha,
				accion: 'cargo' as const,
				precinto: p?.precinto ?? '—',
				bolsas: b.bolsas ?? 0,
				destino: b.tarima ? 'tarima de espera' : `pallet ${numero === null ? '' : `Nº ${numero} · `}${p?.letra ?? '—'} · ${formatoTexto(formato)}`,
			};
			// §12.21: el precinto tiene bolsas en una tarima de espera. Lo que pasa de la tarima viaja como `pallet.unir` —de a
			// partes con menos bolsas que las de la tarima, entera con todas— y, si hay bolsas nuevas del silo, detrás una carga
			// aparte que espera a la unión (dos hechos, dos transacciones: si la carga cae, la tarima pasó igual y la bandeja lo dice).
			const enTarima = b.tarima ? null : (p?.en_tarima ?? null);
			if (enTarima !== null && p !== null) {
				const reparto = b.reparto ?? (b.reparto = nuevoReparto());
				const partes = repartoDeBolsas(b);
				if (partes === null) throw new Error('faltan las bolsas');
				const destinoId = b.nuevo?.pallet_id ?? b.destino?.pallet_id ?? null;
				if (destinoId === null) throw new Error('falta el pallet');
				const numeroDestino = b.nuevo !== null ? (b.nuevo.rotulo?.numero ?? null) : (b.destino?.numero ?? null);
				const lista: MutacionAEncolar[] = [];
				// Lo último pendiente que tocó la tarima o abrió el destino; si no, lo que se congeló al elegirlos.
				const ramaDeLaTarima = padreDeTransicion(pendientes, enTarima.pallet_id, { palletDeEnvasado: palletDeEnvasadoDelCatalogo }) ?? enTarima.padre_mutacion_id ?? undefined;
				if (b.nuevo !== null) {
					// El pallet nuevo nace con el pase: su apertura viaja delante y espera, además del cierre pendiente de su letra y
					// formato, a lo último pendiente que tocó la tarima, para que la unión no encuentre una tarima vacía.
					lista.push(apertura(b.nuevo, b.nuevo.mutacion_id, p.letra, formato, b.personas_pallet, b.registrado_ms, null, b.nuevo.rotulo, null, ramaDeLaTarima));
				}
				const padreDeLaUnion =
					b.nuevo !== null
						? b.nuevo.mutacion_id
						: (padreDeTransicion(pendientes, enTarima.pallet_id, { destinoId, palletDeEnvasado: palletDeEnvasadoDelCatalogo }) ?? enTarima.padre_mutacion_id ?? b.destino?.padre_mutacion_id ?? undefined);
				const unionId = partes.nuevas > 0 ? reparto.mutacion_id : b.mutacion_id;
				lista.push({
					id: unionId,
					personas: [...b.personas],
					tipo: 'pallet.unir',
					payload: armarPayloadPalletUnir(b, destinoId),
					entidad: 'pallet',
					registro_id: enTarima.pallet_id,
					...padreDe(padreDeLaUnion),
					resumen: {
						entidad: 'pallet',
						fecha,
						accion: 'unio',
						es_tarima: true,
						letra: null,
						formato_bolsa_g: formato,
						bolsas: partes.de_la_tarima,
						numero: null,
						quedan: partes.quedan,
						destino_numero: numeroDestino,
					},
				});
				if (partes.nuevas > 0) {
					lista.push({
						id: b.mutacion_id,
						personas: [...b.personas],
						tipo: 'envasado.crear',
						payload: armarPayloadEnvasadoCrear(b, destinoId, partes.nuevas),
						entidad: 'envasado',
						registro_id: b.envasado_id,
						padre_id: unionId,
						resumen: { ...resumen, bolsas: partes.nuevas },
					});
				}
				return lista;
			}
			if (b.nuevo !== null) {
				// La apertura lleva la hora de la carga: el pallet nace con lo primero que se le pone. La
				// tarima la arma quien carga, sin letra ni rótulo, en la campaña del precinto.
				const abrir = b.tarima
					? apertura(b.nuevo, b.nuevo.mutacion_id, null, formato, b.personas_pallet.length > 0 ? b.personas_pallet : b.personas, b.registrado_ms, null, null, { campania_id: p?.campania_id ?? campaniaAbiertaId(), llenado_id: p?.llenado_id ?? null })
					: apertura(b.nuevo, b.nuevo.mutacion_id, p?.letra ?? null, formato, b.personas_pallet, b.registrado_ms, null, b.nuevo.rotulo);
				const carga: MutacionAEncolar = {
					id: b.mutacion_id,
					personas: [...b.personas],
					tipo: 'envasado.crear',
					payload: armarPayloadEnvasadoCrear(b, b.nuevo.pallet_id),
					entidad: 'envasado',
					registro_id: b.envasado_id,
					padre_id: b.nuevo.mutacion_id,
					resumen,
				};
				return [abrir, carga];
			}
			const destino = b.destino;
			if (destino === null) throw new Error('falta el pallet');
			return [
				{
					id: b.mutacion_id,
					personas: [...b.personas],
					tipo: 'envasado.crear',
					payload: armarPayloadEnvasadoCrear(b, destino.pallet_id),
					entidad: 'envasado',
					registro_id: b.envasado_id,
					// La apertura local si sigue en la cola; si no, la que se congeló al elegirlo (si
					// cayó mientras tanto, la carga nace caída con ella, como en producción).
					...padreDe(padreDeCarga(pendientes, destino.pallet_id) ?? destino.padre_mutacion_id),
					resumen,
				},
			];
		}
		case 'anular_pallet': {
			const p = b.pallet;
			if (p === null) throw new Error('falta el pallet');
			return [
				{
					id: b.mutacion_id,
					personas: [...b.personas],
					tipo: 'pallet.anular',
					payload: armarPayloadPalletAnular(b),
					entidad: 'pallet',
					registro_id: p.pallet_id,
					...padreDe(padreDeTransicion(pendientes, p.pallet_id, { palletDeEnvasado: palletDeEnvasadoDelCatalogo }) ?? p.padre_mutacion_id),
					resumen: {
						entidad: 'pallet',
						fecha: fechaDeProduccion(b.cerrado_ms ?? ahoraMs()),
						accion: 'anulo',
						es_tarima: p.letra === null,
						letra: (p.letra ?? null) as LetraPallet | null,
						formato_bolsa_g: p.formato_bolsa_g,
						bolsas: p.bolsas,
						numero: numeroDePallet(p),
					},
				},
			];
		}
		case 'rehacer_pallet': {
			// La apertura con el rótulo nuevo y todo lo que colgaba de ella, con ids nuevos y los padres
			// reconstruidos (r. 22); el `pallet_id` y las versiones se conservan porque nunca entraron.
			const rotulo = b.rotulo;
			if (rotulo === null) throw new Error('falta el rótulo');
			return mutacionesAlRehacer(b.apertura, b.hijos, rotulo, generarUlid, enCola).map((m, i) => {
				const resumen = m.resumen as Resumen;
				return {
					id: m.id,
					personas: m.personas,
					tipo: m.tipo,
					payload: m.payload,
					entidad: m.entidad,
					registro_id: m.registro_id,
					...padreDe(m.padre_id),
					resumen: i === 0 && resumen.entidad === 'pallet' ? { ...resumen, numero: rotulo.numero } : resumen,
				};
			});
		}
		case 'cambiar_letra': {
			if (b.muestra_base_id === null || b.letra === null || textoValido(b.motivo) === null) throw new Error('falta la letra o el motivo');
			// Un segundo cambio sobre uno de este teléfono que todavía no subió parte de su versión y
			// cuelga de él: si el primero cae, este nace en la bandeja con `padre_rechazado` en vez de
			// viajar contra una base que el servidor no conoce (2/10).
			const base = b.muestra_base_id;
			const padreMuestra = pendientes.find((f) => f.tipo === 'muestra.letra' && (f.payload as { muestra_id?: unknown }).muestra_id === base)?.id;
			return [
				{
					id: b.mutacion_id,
					personas: [...b.personas],
					tipo: 'muestra.letra',
					payload: { muestra_id: b.muestra_id, corrige_a: base, letra_asignada: b.letra, motivo: textoValido(b.motivo), persona_id: b.personas[0] },
					entidad: 'muestra',
					registro_id: b.muestra_id,
					...padreDe(padreMuestra),
					resumen: { entidad: 'muestra', accion: 'cambio_letra', fecha: fechaDeProduccion(ahoraMs()), silo: b.silo_numero ?? '—', precinto: b.precinto ?? '—', letra: b.letra },
				},
			];
		}
		case 'nota_precinto':
			return [notaParaLaCola(b.mutacion_id, b.nota_id, b.llenado_id, b.precinto, b.texto, b.personas)];
		// §12.18: cuelga de lo último pendiente que toque el pallet de la carga (su creación, otra corrección, el cierre):
		// así un cierre que todavía no viajó llega antes y el servidor rehace su testigo; si no hay, de lo fijado al elegir.
		case 'corregir_carga':
		case 'anular_carga': {
			const c = b.carga;
			if (c === null) throw new Error('falta la carga');
			const padre =
				masNuevaDe(pendientes, [
					padreDeTransicion(pendientes, c.pallet_id, { palletDeEnvasado: palletDeEnvasadoDelCatalogo }),
					padreDeTransicion(pendientes, c.pallet_directo_id, { palletDeEnvasado: palletDeEnvasadoDelCatalogo }),
				]) ?? c.padre_mutacion_id;
			const destino = `pallet ${c.pallet_numero === null ? '' : `Nº ${c.pallet_numero} · `}${formatoTexto(c.formato_bolsa_g)}`;
			const fecha = fechaDeProduccion(ahoraMs());
			if (b.tipo === 'corregir_carga') {
				return [
					{
						id: b.mutacion_id,
						personas: [...b.personas],
						tipo: 'carga.corregir',
						payload: armarPayloadCargaCorregir(b),
						entidad: 'envasado',
						registro_id: b.envasado_id,
						...padreDe(padre),
						resumen: { entidad: 'envasado', fecha, accion: 'corrigio', precinto: c.precinto ?? '—', bolsas: b.bolsas ?? 0, destino },
					},
				];
			}
			return [
				{
					id: b.mutacion_id,
					personas: [...b.personas],
					tipo: 'carga.anular',
					payload: armarPayloadCargaAnular(b),
					entidad: 'envasado',
					registro_id: c.envasado_id,
					...padreDe(padre),
					resumen: { entidad: 'envasado', fecha, accion: 'anulo', precinto: c.precinto ?? '—', bolsas: c.cant_bolsas, destino },
				},
			];
		}
		default: {
			const p = b.pallet;
			if (p === null) throw new Error('falta el pallet');
			return [
				{
					id: b.mutacion_id,
					personas: [...b.personas],
					tipo: 'pallet.cerrar',
					payload: armarPayloadPalletCerrar(b),
					entidad: 'pallet',
					registro_id: p.pallet_id,
					...padreDe(padreDeTransicion(pendientes, p.pallet_id, { palletDeEnvasado: palletDeEnvasadoDelCatalogo }) ?? p.padre_mutacion_id),
					resumen: {
						entidad: 'pallet',
						fecha: fechaDeProduccion(b.cerrado_ms ?? ahoraMs()),
						accion: 'cerro',
						es_tarima: false,
						letra: (p.letra ?? null) as LetraPallet | null,
						formato_bolsa_g: p.formato_bolsa_g,
						bolsas: p.bolsas,
						numero: numeroDePallet(p),
					},
				},
			];
		}
	}
}

/**
 * Lo elegido puede haber dejado de valer mientras se cargaba: otro teléfono abrió o cerró un
 * pallet, otro borrador de este mismo teléfono se confirmó antes, o entró una carga al pallet que
 * se está cerrando. Se comprueba contra la proyección de AHORA antes de encolar, y si cambió se
 * vuelve al paso que corresponde en vez de mandar algo que el servidor va a rechazar
 * (`pallet_abierto_repetido`, `pallet_cerrado`, `composicion_cambio`; Codex, ronda del diff, 14/9).
 */
export function pasoDesactualizado(b: BorradorEmbolsado): { paso: number; aviso: string } | null {
	const pasos = pasosDe(b);
	// §12.18: la carga se mira al día —otro teléfono la pudo corregir o anular, el pallet pudo salir o entrar a una mezcla—
	// y se vuelve al paso con el porqué, en vez de mandar algo que el servidor va a rechazar.
	if (b.tipo === 'corregir_carga' || b.tipo === 'anular_carga') {
		const paso = pasos.indexOf(b.tipo === 'corregir_carga' ? 'bolsas_carga' : 'motivo_carga');
		if (b.carga === null) return null;
		const ahora = cargaAlDia(b.carga);
		if (ahora === null) return { paso, aviso: 'Esa carga ya no está: la anularon, o su pallet se tiró. Volvé al inicio.' };
		const traba = porQueNoSeCorrige(ahora, desdeParaCorregir());
		if (traba !== null) {
			b.carga = ahora;
			return { paso, aviso: `Esa carga ${traba}.` };
		}
		if (ahora.envasado_id !== b.carga.envasado_id || ahora.pallet_bolsas !== b.carga.pallet_bolsas || ahora.pallet_estado !== b.carga.pallet_estado) {
			const antes = b.carga.cant_bolsas;
			b.carga = ahora;
			if (ahora.cant_bolsas !== antes) return { paso, aviso: `Mientras tanto esa carga cambió: ahora tiene ${ahora.cant_bolsas} bolsas.` };
		}
		if (b.tipo === 'anular_carga') {
			if (esLaUnicaDeUnCerrado(ahora)) return { paso, aviso: 'Es la única carga del pallet: para sacarla entera, la oficina anula el pallet.' };
			if (motivoDeAnularCarga(b) === null) return { paso, aviso: 'Falta por qué se anula.' };
			return null;
		}
		if (b.bolsas === ahora.cant_bolsas) return { paso, aviso: 'Es lo que ya tiene: no hay nada que corregir.' };
		const precinto = precintoDeLaCarga(ahora);
		if (b.bolsas !== null && precinto !== null && superaLaToleranciaAlCorregir(precinto, ahora.cant_bolsas, b.bolsas, ahora.formato_bolsa_g)) {
			return { paso, aviso: `Con lo que ya se envasó de este precinto, esas bolsas pasan el tope: ${BOLSAS_DEL_TOPE} bolsas (o el ${POR_CIENTO_DEL_TOPE} %) por encima del silo.` };
		}
		return null;
	}
	// El rótulo elegido pudo quedar tomado mientras tanto: otro borrador de este teléfono se
	// confirmó antes con el mismo papel (r. 22).
	const rotuloTrabado = (): { paso: number; aviso: string } | null => {
		const traba = trabaDelRotulo(b as ConRotulo);
		return traba === null ? null : { paso: pasos.indexOf('rotulo'), aviso: `${traba} Elegí otro rótulo.` };
	};
	if (b.tipo === 'embolsar' && b.tarima) {
		const formato = b.formato_bolsa_g ?? FORMATO_PREMARCADO;
		precintoAlDia(b);
		// Mientras tanto pudo llegarle la letra (va a un pallet: `precinto_con_letra`) o pudo abrírsele
		// otra tarima desde otro teléfono (`tarima_repetida`): se vuelve a elegir.
		if (b.precinto !== null && !precintosParaTarima().some((p) => p.llenado_id === b.precinto?.llenado_id)) {
			const conLetra = precintosParaEmbolsar().find((p) => p.llenado_id === b.precinto?.llenado_id);
			return {
				paso: pasos.indexOf('precinto'),
				aviso: conLetra
					? `Al precinto ${b.precinto.precinto} ya le llegó la letra (${conLetra.letra}): va a un pallet, no a una tarima.`
					: `El precinto ${b.precinto.precinto} ya tiene su tarima, o dejó de estar cerrado: elegí otro.`,
			};
		}
		if (b.precinto !== null && superaLaTolerancia(b.precinto, b.bolsas, formato)) {
			return { paso: pasos.indexOf('bolsas'), aviso: `Con lo que ya se envasó de este precinto, estas bolsas pasan el tope: ${BOLSAS_DEL_TOPE} bolsas (o el ${POR_CIENTO_DEL_TOPE} %) por encima del silo.` };
		}
		return null;
	}
	if (b.tipo === 'embolsar') {
		// El precinto tiene que seguir para embolsar: calidad pudo anular la muestra y producción pudo
		// descartar el silo (`precinto_sin_letra`, `precinto_descartado`; Codex, ronda de la v5 entera,
		// 15/9).
		if (b.precinto !== null && !precintosParaEmbolsar().some((p) => p.llenado_id === b.precinto?.llenado_id)) {
			const precinto = b.precinto.precinto;
			b.precinto = null;
			b.destino = null;
			b.nuevo = null;
			return { paso: pasos.indexOf('precinto'), aviso: `El precinto ${precinto} dejó de estar para embolsar: calidad le sacó la letra o el silo se descartó. Elegí otro.` };
		}
		// El precinto se mira al día ANTES que el pallet: si mientras tanto cambió de letra
		// (`pallet_de_otra_letra`) o se embolsó con el otro formato, el pallet que corresponde es otro.
		const cambio = precintoAlDia(b);
		if (cambio !== null) return { paso: pasos.indexOf(cambio.paso), aviso: cambio.texto };
		const formato = b.formato_bolsa_g ?? FORMATO_PREMARCADO;
		const abierto = b.precinto === null ? undefined : palletAbiertoPara(b.precinto.letra, formato);
		if (b.nuevo !== null && abierto) {
			return { paso: pasos.indexOf('pallet'), aviso: `Mientras tanto se abrió un pallet de ${b.precinto?.letra ?? ''} · ${formatoTexto(formato)}: la carga va a ese.` };
		}
		if (b.destino !== null && abierto?.pallet_id !== b.destino.pallet_id) {
			return { paso: pasos.indexOf('pallet'), aviso: 'El pallet elegido ya no está en armado: elegí de nuevo.' };
		}
		if (b.destino === null && b.nuevo === null) return { paso: pasos.indexOf('pallet'), aviso: 'Falta el pallet: elegilo.' };
		// El destino se congeló al elegirlo, pero su apertura es la de AHORA: gestión pudo correrla, y
		// una carga fechada antes iría a `carga_antes_del_pallet` (Codex, ronda de la v5 entera, 15/9).
		if (b.destino !== null && abierto && (abierto.version_id !== b.destino.version_id || abierto.abierto_en !== b.destino.abierto_en)) {
			b.destino = palletVisto(abierto);
			if (!cargaDentroDelPallet(b.registrado_ms, b.destino)) {
				return { paso: pasos.indexOf('hora'), aviso: 'Mientras tanto la oficina corrió la apertura del pallet: la hora de la carga tiene que ser después de esa.' };
			}
		}
		// Otro teléfono pudo cargar del mismo precinto mientras tanto: el saldo de ahora es el que manda (§12.21: solo las
		// bolsas nuevas cuentan; las de la tarima ya están envasadas).
		if (b.precinto !== null && superaLaTolerancia(b.precinto, bolsasNuevas(b), formato)) {
			return { paso: pasos.indexOf('bolsas'), aviso: `Con lo que ya se envasó de este precinto, estas bolsas pasan el tope: ${BOLSAS_DEL_TOPE} bolsas (o el ${POR_CIENTO_DEL_TOPE} %) por encima del silo.` };
		}
		// §12.21: el pase no puede quedar antes de la apertura de la tarima ni de su última carga (la de ahora).
		if (b.precinto?.en_tarima && !cierreDePalletValido(b.registrado_ms, b.precinto.en_tarima)) {
			return { paso: pasos.indexOf('hora'), aviso: 'La hora tiene que ser después de la apertura de la tarima y de su última carga.' };
		}
		return b.nuevo !== null ? rotuloTrabado() : null;
	}
	if (b.tipo === 'rehacer_pallet') return rotuloTrabado();
	if (b.tipo === 'cambiar_letra') {
		// La calidad nueva tiene que llegar al pallet: un cambio de letra con bolsas de otra letra queda en «calidad cambió».
		if (b.letra === null) return { paso: pasos.indexOf('nueva_letra'), aviso: 'Falta la calidad nueva.' };
		if (textoValido(b.motivo) === null) return { paso: pasos.indexOf('motivo_letra'), aviso: 'Falta por qué cambia.' };
		// La muestra base es la de AHORA (el catálogo con la cola encima): calidad, la oficina u otro
		// teléfono pudieron versionarla mientras se escribía el motivo, y el cambio viajaba con un
		// `corrige_a` viejo que el servidor rechaza (CAS); si ya quedó en la letra elegida, diría
		// `ya_estaba_asi`: se vuelve a elegir (2/10).
		const ahora = precintosParaEmbolsar().find((p) => p.llenado_id === b.llenado_id);
		if (ahora?.muestra_id !== undefined && ahora.muestra_id !== b.muestra_base_id) {
			b.muestra_base_id = ahora.muestra_id;
			b.letra_actual = letraDeMuestraDe(ahora.letra);
			if (b.letra_actual === b.letra) {
				b.letra = null;
				return { paso: pasos.indexOf('nueva_letra'), aviso: `Mientras tanto el precinto ya pasó a ${NOMBRE_LETRA_MUESTRA[b.letra_actual ?? ''] ?? b.letra_actual}: elegí otra calidad, o volvé.` };
			}
		}
		return null;
	}
	if (b.tipo === 'nota_precinto') return textoValido(b.texto) === null ? { paso: pasos.indexOf('texto_nota'), aviso: 'Falta la nota.' } : null;
	if (b.pallet === null) return null;
	const actual = palletsAbiertos().find((p) => p.pallet_id === b.pallet?.pallet_id);
	if (!actual) return { paso: pasos.indexOf('cual'), aviso: b.tipo === 'anular_pallet' ? 'Ese pallet ya no está en armado: lo cerró o lo tiró otro teléfono, o la oficina.' : 'Ese pallet ya no está en armado: lo cerró otro teléfono o la oficina.' };
	const foto = palletVisto(actual);
	// Cambió lo que se vio: bolsas, versión, o la hora de una carga corregida —la última, que decide la
	// hora del cierre, y la primera, que decide el vencimiento del rótulo— (Codex, 15/9).
	const cambio = foto.bolsas !== b.pallet.bolsas || foto.version_id !== b.pallet.version_id || foto.ultima_carga_en !== b.pallet.ultima_carga_en || foto.primera_carga_en !== b.pallet.primera_carga_en;
	if (b.tipo === 'anular_pallet') {
		// Tirar no lleva testigo: lo que se vio se refresca para la hora y el resumen, sin volver atrás;
		// pero la hora de tirarlo tiene que seguir después de la última carga de ahora.
		if (cambio) elegirPallet(b, actual);
		if (!cierreDePalletValido(b.cerrado_ms, b.pallet)) return { paso: pasos.indexOf('hora'), aviso: 'Mientras tanto le entró una carga: la hora de tirarlo tiene que ser después de esa.' };
		return null;
	}
	if (cambio) {
		// El testigo de composición es lo que este teléfono vio: si lo que ve cambió, se vuelve a mirar.
		elegirPallet(b, actual);
		return { paso: pasos.indexOf('revisar'), aviso: 'El pallet cambió mientras lo cerrabas: revisalo y volvé a mantener apretado.' };
	}
	return null;
}

async function confirmar(b: BorradorEmbolsado): Promise<void> {
	if (comprometiendo.has(b.capture_id)) return;
	comprometiendo.add(b.capture_id);
	try {
		// Gestión pudo cambiar la estación del teléfono con la carga a medias: lo encolado iría a
		// `estacion_no_autorizada`. El borrador queda; se ofrece de nuevo si el teléfono vuelve.
		if (app.sesion?.estacion !== 'envasado') {
			irA({ tipo: 'sin-estacion', estacion: app.sesion?.estacion ?? null });
			return;
		}
		// v6 (§12.5): un precinto que dejó de tener muestra vigente (calidad la anuló, el silo se descartó o se
		// anuló) no tiene calidad que cambiar: el borrador se va y el inicio lo dice.
		if (b.tipo === 'cambiar_letra' && !precintosParaEmbolsar().some((p) => p.llenado_id === b.llenado_id)) {
			await descartarBorrador(b).catch(() => null);
			irA({ tipo: 'envasado-inicio', aviso: `El precinto ${b.precinto ?? ''} ya no tiene muestra para cambiar: calidad la anuló o el silo se descartó.` });
			return;
		}
		const desactualizado = pasoDesactualizado(b);
		if (desactualizado) {
			await irAlPaso(b, desactualizado.paso, desactualizado.aviso);
			return;
		}
		// La campaña viaja con el rótulo (r. 22): una apertura sin rótulo no sale de la revisión.
		const abre = b.tipo === 'rehacer_pallet' || (b.tipo === 'embolsar' && b.nuevo !== null && !b.tarima);
		if (abre && rotuloDe(b as ConRotulo) === null) {
			await irAlPaso(b, pasosDe(b).indexOf('rotulo'), 'Falta el rótulo del pallet.');
			return;
		}
		// Al rehacer, el árbol se vuelve a leer de la bandeja de AHORA: si otra pestaña ya lo rehízo o
		// el encargado lo quitó, no hay nada que reencolar; si cayeron descendientes después de armar
		// el borrador, también vuelven (Codex, ronda del diff de 7b, 15/9).
		let enCola: Set<string> | undefined;
		if (b.tipo === 'rehacer_pallet') {
			const arbol = await incidenteConDescendientes(app.bd, b.apertura.id);
			if (!arbol) {
				await descartarBorrador(b).catch(() => null);
				irA({ tipo: 'envasado-inicio', aviso: 'Ese pallet ya se volvió a mandar (o la oficina lo quitó de la bandeja): no hay nada que rehacer.' });
				return;
			}
			b.hijos = arbol.hijos.map(caidaDe);
			enCola = arbol.enCola;
		}
		// Los padres se eligen contra la cola de AHORA (§5.7 r. 20): lo que quedó pendiente
		// desde que se empezó a cargar también cuenta.
		// Las de embolsado y las muestras: un cambio de calidad cuelga del cambio pendiente que corrige.
		const pendientes = await pendientesDe(app.bd, new Set<Entidad>([...ENTIDADES_EMBOLSADO, 'muestra']));
		const mutaciones = paraLaCola(b, pendientes, enCola);
		// Todo lo que deja una confirmación entra en UNA transacción: la carga nunca queda en
		// la cola sin la apertura del pallet nuevo que la precede; al rehacer, la apertura caída y
		// lo que colgaba de ella salen de la bandeja en esa misma transacción.
		const incidentes = b.tipo === 'rehacer_pallet' ? [b.apertura.id, ...b.hijos.map((h) => h.id)] : [];
		try {
			await encolar(app.bd, mutaciones, [b.capture_id], new Date(ahoraMs()).toISOString(), [], incidentes);
		} catch (error) {
			if (error instanceof RotuloEnCola) {
				const elegido = abre ? rotuloDe(b as ConRotulo) : null;
				if (b.tipo === 'rehacer_pallet' && elegido !== null && error.numero !== elegido.numero) {
					// El papel tomado es el de un pallet que colgaba de este (los rótulos de la bandeja ya no se
					// proponen, pero uno elegido antes pudo quedar): ese pallet se rehace aparte.
					await irAlPaso(
						b,
						pasosDe(b).indexOf('revisar'),
						`El rótulo Nº ${error.numero} de un pallet que colgaba de este ya está en otra apertura que espera subir. Quitá de la bandeja ese pallet y lo que cuelga de él, mandá este, y volvé a abrir aquel con otro rótulo.`,
					);
					return;
				}
				// Otra apertura pendiente lleva ese papel: se vuelve al rótulo en vez de mandar un `rotulo_usado`.
				await irAlPaso(b, pasosDe(b).indexOf('rotulo'), `El rótulo Nº ${error.numero} ya está en una apertura que espera subir. Elegí otro.`);
				return;
			}
			if (error instanceof IncidenteResuelto) {
				await descartarBorrador(b).catch(() => null);
				irA({ tipo: 'envasado-inicio', aviso: 'Ese pallet ya se volvió a mandar desde otra pestaña: no se mandó dos veces.' });
				return;
			}
			if (error instanceof BorradorYaConfirmado) {
				// Una copia vieja de este borrador en otra pestaña: ya se mandó; no se manda dos veces.
				await descartarBorrador(b).catch(() => null);
				irA({ tipo: 'envasado-inicio', aviso: 'Ese registro ya se mandó desde otra pestaña: no se mandó dos veces.' });
				return;
			}
			throw error;
		}
		app.rotulosEnBorradores.delete(b.capture_id);
		// La proyección se rehace ANTES de volver: el pallet nuevo tiene que aparecer ya.
		recalcularPallets(await mutacionesDeEmbolsado(app.bd, app.preferencias.catalogos_hora_servidor));
		await recalcularRotulosEnBandeja();
		// v6: la letra nueva y la nota se ven ya, antes del sync.
		recalcularParaMuestra(await mutacionesDeCalidad(app.bd, app.preferencias.catalogos_hora_servidor));
		recalcularAvisos(await mutacionesDeAvisos(app.bd, app.preferencias.catalogos_hora_servidor));
		app.dispararSync();
		irA({ tipo: 'envasado-listo', borrador: b });
	} catch (error) {
		console.error('no se pudo guardar el registro de envasado', error);
		irA({ tipo: 'envasado-inicio', aviso: 'No se pudo guardar. Probá de nuevo.' });
	} finally {
		comprometiendo.delete(b.capture_id);
	}
}

/** La pantalla de «listo», con lo que quedó guardado; tras una carga que llega a 36, propone cerrar. */
export function pintarListoEnvasado(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorEmbolsado): void {
	// El número es el del rótulo que se le puso (r. 22): se sabe desde antes de encolar.
	const reparto = b.tipo === 'embolsar' && !b.tarima && b.precinto?.en_tarima ? repartoDeBolsas(b) : null;
	const titulo =
		b.tipo === 'embolsar'
			? b.tarima
				? 'Tarima en espera'
				: `${reparto !== null ? (reparto.nuevas > 0 ? `${reparto.de_la_tarima} de la tarima y ${reparto.nuevas} nuevas` : `${reparto.de_la_tarima} bolsas de la tarima`) : 'Cargado'} ${b.nuevo !== null ? `en el pallet nuevo Nº ${b.nuevo.rotulo?.numero ?? '—'}` : `en el pallet ${b.destino ? nombreDePallet(b.destino) : ''}`}`.trim()
			: b.tipo === 'rehacer_pallet'
				? `Pallet Nº ${b.rotulo?.numero ?? '—'} · vuelve a subir`
				: b.tipo === 'anular_pallet'
					? `${b.pallet?.letra === null ? 'Tarima tirada' : `Pallet ${b.pallet ? nombreDePallet(b.pallet) : ''} tirado`} · ${b.motivo ?? ''}`.trim()
					: b.tipo === 'cambiar_letra'
						? `Calidad cambiada · precinto ${b.precinto ?? ''}`.trim()
						: b.tipo === 'nota_precinto'
							? 'Nota anotada'
							: b.tipo === 'corregir_carga'
								? 'Carga corregida'
								: b.tipo === 'anular_carga'
									? 'Carga anulada'
									: `Pallet cerrado · ${b.pallet ? nombreDePallet(b.pallet) : ''}`.trim();
	cuerpo.appendChild(el('div', { class: 'done' }, titulo));
	const resumen = el('div', { class: 'ticket' });
	for (const [etiqueta, valor] of filasDe(b)) {
		const fila = el('div', { class: 'row' });
		fila.appendChild(el('span', undefined, etiqueta));
		fila.appendChild(el('b', undefined, valor));
		resumen.appendChild(fila);
	}
	cuerpo.appendChild(resumen);
	cuerpo.appendChild(el('div', { class: 'qsub despues' }, app.enLinea ? 'Subiendo.' : 'Sin señal: sube solo cuando haya.'));
	if (b.tipo === 'embolsar' && b.tarima) cuerpo.appendChild(el('div', { class: 'qsub despues' }, 'Cuando calidad le ponga la letra, el precinto vuelve a «Con letra, sin embolsar» con estas bolsas en espera: embolsarlo las reparte.'));
	if (reparto !== null && reparto.quedan > 0) cuerpo.appendChild(el('div', { class: 'qsub despues' }, `Quedan ${reparto.quedan} bolsas en espera: el precinto sigue en la lista para repartirlas.`));
	// v6 (§12.5): el precinto ya está en la lista de su letra nueva, con o sin señal.
	if (b.tipo === 'cambiar_letra' && b.letra !== null) {
		cuerpo.appendChild(el('div', { class: 'qsub despues' }, `Lo que se embolse de este precinto va a un pallet ${letraDestinoDe(b.letra) ?? '—'}.`));
	}
	if (b.tipo === 'embolsar' && !b.tarima) {
		// El pallet que acaba de recibir la carga (o la tarima), ya proyectado: si llegó a 36, se propone cerrarlo.
		const palletId = b.nuevo?.pallet_id ?? b.destino?.pallet_id ?? null;
		const pallet = palletId === null ? undefined : palletsAbiertos().find((p) => p.pallet_id === palletId);
		// Cuántas lleva ahora el pallet (boceto «listo»): es lo que dice qué tan cerca está de las 36.
		if (pallet) {
			const lleva = el('div', { class: 'row' });
			lleva.appendChild(el('span', undefined, 'El pallet lleva'));
			lleva.appendChild(el('b', undefined, `${pallet.bolsas} bolsas · ${kgConDecimales(gramosDeCarga(pallet.bolsas, pallet.formato_bolsa_g))} kg`));
			resumen.appendChild(lleva);
		}
		if (pallet && pallet.bolsas >= BOLSAS_ESTANDAR) {
			cuerpo.appendChild(
				flag(
					pallet.bolsas === BOLSAS_ESTANDAR ? 'ok' : 'warn',
					pallet.bolsas === BOLSAS_ESTANDAR ? `El pallet llegó a ${BOLSAS_ESTANDAR} bolsas.` : `El pallet tiene ${pallet.bolsas} bolsas: más de ${BOLSAS_ESTANDAR}.`,
					'¿Lo cerrás?',
				),
			);
			const cerrar = el('button', { class: 'enlace', type: 'button' }, 'Cerrar el pallet');
			cerrar.onclick = () => acciones.cerrarPallet(pallet);
			cuerpo.appendChild(cerrar);
		}
	}
	// Dos botones en el pie van como en «retomar»: el secundario estira (`anchoflex`) y el
	// principal a su derecha; dos `ancho` no entran en un teléfono (visto el 14/9).
	// Un desvío (cambiar la calidad, una nota) descartó el embolsado desde el que se entró: se ofrece
	// volver a embolsar ESE precinto, leído de la lista de ahora —con su letra nueva—, mientras siga
	// ahí (si quedó entero o sin letra, solo «volver al inicio»).
	const precintoFresco = b.tipo === 'cambiar_letra' || b.tipo === 'nota_precinto' ? precintosParaElegir().find((p) => p.llenado_id === b.llenado_id) : undefined;
	const botones: Array<[string, string, () => void]> =
		b.tipo === 'cerrar_pallet' || b.tipo === 'rehacer_pallet' || b.tipo === 'anular_pallet' || b.tipo === 'corregir_carga' || b.tipo === 'anular_carga'
			? [['btn go ancho', 'Volver al inicio', () => acciones.inicioEnvasado()]]
			: b.tipo === 'cambiar_letra' || b.tipo === 'nota_precinto'
				? precintoFresco
					? [
							['btn ghost anchoflex', 'Volver al inicio', () => acciones.inicioEnvasado()],
							['btn go', 'Embolsar este precinto', () => acciones.embolsar(precintoFresco)],
						]
					: [['btn go ancho', 'Volver al inicio', () => acciones.inicioEnvasado()]]
				: [
						['btn ghost anchoflex', 'Volver al inicio', () => acciones.inicioEnvasado()],
						['btn go', b.tipo === 'embolsar' ? 'Otro precinto' : 'Embolsar un precinto', () => acciones.embolsar()],
					];
	for (const [clase, texto, alTocar] of botones) {
		const boton = el('button', { class: clase, type: 'button' }, texto);
		boton.onclick = alTocar;
		pieNodo.appendChild(boton);
	}
}

// ── Rehacer con otro rótulo una apertura que el servidor rechazó (§5.7 r. 22) ──

const rehaciendo = new Set<string>();

/** Una fila de la bandeja como mutación caída, lo que el borrador de rehacer guarda. */
function caidaDe(f: FilaOutbox): MutacionCaida {
	return {
		id: f.id,
		tipo: f.tipo,
		payload: f.payload,
		entidad: f.entidad === 'envasado' ? 'envasado' : 'pallet',
		registro_id: f.registro_id,
		personas: f.personas,
		padre_id: f.padre_id ?? null,
		resumen: f.resumen,
	};
}

/**
 * Desde la bandeja: la apertura caída por el rótulo y todo lo que colgaba de ella (cargas, cierre)
 * vuelven como un borrador que solo pregunta el rótulo correcto; al confirmar se encolan de nuevo
 * con ids nuevos y los incidentes salen de la bandeja. Si ya se había empezado a rehacer, se retoma
 * ese borrador (uno por incidente: `capture_id` es el id de la apertura caída).
 */
export async function rehacerAperturaRechazada(incidente: FilaOutbox): Promise<void> {
	if (rehaciendo.has(incidente.id)) return;
	rehaciendo.add(incidente.id);
	try {
		const arbol = await incidenteConDescendientes(app.bd, incidente.id);
		if (!arbol) {
			irA({ tipo: 'pendientes' });
			return;
		}
		const borrador = nuevoBorradorRehacerPallet(caidaDe(arbol.incidente), arbol.hijos.map(caidaDe), incidente.motivo ?? '');
		arrancarEn(borrador);
		await guardarBorrador(borrador);
		irA({ tipo: 'envasado', borrador });
	} finally {
		rehaciendo.delete(incidente.id);
	}
}
